import { evaluatePlatformReadiness } from '../../../shared/posting-readiness';
import { BaseService } from '../base/BaseService';
import { ServiceError } from '../base/ServiceError';
import type { QueryOptions, ServiceResponse } from '../base/ServiceTypes';
import { mediaService } from '../media/mediaService';
import type { SecureImageData } from '../media/mediaService';

import { targetCoverService } from './TargetCoverService';
import { getEffectiveLockedFields } from './lockedFieldsClient';

import type { Database } from '@/integrations/supabase/types';
import { aggregateTargetAwareMetrics } from '@/lib/analytics/target-metric-aggregation';
import type {
  LegacyPostMetricRow,
  TargetPostMetricRow,
} from '@/lib/analytics/target-metric-aggregation';
import {
  LINKEDIN_DESTINATION_REQUIRED,
  LINKEDIN_DESTINATION_REQUIRED_MESSAGE,
  linkedInDestinationChoiceMissing,
  requestLinkedInDestinationChoice,
} from '@/lib/connections/destination-options';
import { inferMediaType } from '@/lib/media/infer-media-type';
import { assertEditAllowed } from '@/lib/posts/edit-guardrail';
import { resolvePlatformCaption } from '@/lib/posts/platform-caption';
import {
  enabledPlatforms,
  getDisabledPlatforms,
  withPlatformEnabled,
} from '@/lib/posts/platform-enablement';
import {
  type PlatformSchedulesMap,
  effectivePlatformStatus,
  parsePlatformSchedules,
  rollupRowStatus,
} from '@/lib/posts/platform-schedule';
import { type SchedulePartition, partitionScheduleReadiness } from '@/lib/posts/schedule-partition';
import { withUnsupportedPublishBlock } from '@/lib/posts/unsupported-publish';
import { PREVIEW_ACCOUNT_MESSAGE, isPreviewAccountFlagged } from '@/lib/preview-account';
import { logger } from '@/utils/logger';

// TextT aliases for better readability
export type PostRow = Database['public']['Tables']['social_posts']['Row'];
type PostInsert = Database['public']['Tables']['social_posts']['Insert'];
type PostUpdate = Database['public']['Tables']['social_posts']['Update'];

// eng-review High 4: a staggered row whose row-level scheduled_at falls outside
// a calendar window can still have a per-platform time inside it. PostgREST
// can't range-test the arbitrary keys of platform_schedules jsonb per-platform,
// so the date-window OR pulls in EVERY still-pending staggered row (non-null
// platform_schedules + a status that hasn't fully finalized); the client then
// buckets those by their effective per-platform times. NULL platform_schedules
// rows never match these terms, so the common (non-staggered) path is byte-for-
// byte unchanged.
const PLATFORM_SCHEDULE_WINDOW_TERMS: readonly string[] = [
  'and(platform_schedules.not.is.null,status.eq.scheduled)',
  'and(platform_schedules.not.is.null,status.eq.partially_scheduled)',
];

type PostMediaKeys = {
  disabledPlatforms?: unknown;
  perPlatform?: Record<
    string,
    {
      aspectRatio?: string | null;
      variantUrl?: string | null;
      transformFailed?: boolean | null;
      /** Task A4. */
      placement?: string | null;
    }
  >;
};

function mediaKeysObject(value: unknown): PostMediaKeys | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as PostMediaKeys)
    : null;
}

function perPlatformConfigFor(mediaKeys: unknown, platform: string) {
  return mediaKeysObject(mediaKeys)?.perPlatform?.[platform] ?? null;
}

function newChildId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `child-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function initialChildManifest(mediaUrls: string[]) {
  return mediaUrls.map((url, ordinal) => ({
    childId: newChildId(),
    ordinal,
    url,
    kind: /\.(mp4|mov|webm|m4v)(\?|$)/i.test(url) ? 'video' : 'image',
    sourceFingerprint: url,
    renditions: {},
  }));
}

// Define metrics type for posts
export interface PostMetrics {
  totalLikes: number;
  totalComments: number;
  totalShares: number;
  totalSaves: number;
  totalViews: number;
  engagementRate: number;
}

export interface PostActivityDateRow {
  created_at: string | null;
  published_at: string | null;
  scheduled_at: string | null;
}

// Input types for create methods
export interface CreatePostInput {
  content: string;
  platform?: string;
  platforms?: string[];
  accountId?: string;
  scheduledAt?: string;
  mediaUrls?: string[];
  productId?: string;
  projectId?: string;
  collectionId?: string;
  campaignId?: string;
  format?: string;
  sourceArticleId?: string | null;
  sourceUrl?: string | null;
  formatHint?: string | null;
}

export interface CreateDraftInput extends Omit<CreatePostInput, 'scheduledAt'> {
  generatedImages?: SecureImageData[];
}

/**
 * Input for `createPostFromConcept` — the one organic write that carries
 * Concept lineage. `conceptRenditionId` is required and is never inferred:
 * the caller (the Concept Workbench's "Publish organically" action) already
 * has it in scope. There is deliberately no `campaignId`/`projectId` field
 * here — a Concept's parent is a Brief (`projects.kind = 'brief'`), not a
 * campaign-kind project, so `campaign_id` is never stamped from this path.
 */
export interface CreatePostFromConceptInput {
  conceptRenditionId: string;
  content: string;
  platform?: string;
  platforms?: string[];
  mediaUrls?: string[];
  generatedImages?: SecureImageData[];
  productId?: string;
  collectionId?: string;
}

// Filter types for querying posts
export interface PostFilters {
  /** Leave out failures a person has cleared from the needs-attention count. */
  uncleared?: boolean;
  platform?: string; // Changed from platforms to platform for consistency
  platforms?: string[];
  /** Single status (`.eq`) or a status set (`.in`) — e.g. ['failed', 'failed_connection_removed']. */
  status?: string | string[];
  startDate?: string;
  endDate?: string;
  hasMedia?: boolean;
  productId?: string;
  projectId?: string;
  collectionId?: string;
  campaignId?: string;
  accountId?: string;
  competitorId?: string;
  /** Free-text match against the post body. Case-insensitive substring. */
  search?: string;
}

export interface DraftIdFilters {
  platforms?: string[];
  createdAfter?: string;
  limit?: number;
}

export interface PlanDraftOnCalendarOptions {
  collectionId?: string | null;
  campaignId?: string | null;
  /** The exact account the post will publish as, frozen on the post (`destination_*_ids`). */
  destination?: { platform: string; connectionId: string; resourceId: string | null };
}

/**
 * Single home for PostFilters.status semantics — `.eq` for a single status,
 * `.in` for a status set. Every query path that honors `filters.status`
 * (getUserPostsPaginated, getFilteredPosts, countPosts) goes through here so
 * a future semantics change can't silently diverge between them.
 */
function applyStatusFilter<
  Q extends {
    eq: (column: 'status', value: string) => Q;
    in: (column: 'status', values: string[]) => Q;
  },
>(query: Q, status: string | string[]): Q {
  return Array.isArray(status) ? query.in('status', status) : query.eq('status', status);
}

/**
 * Free-text filter over the post body.
 *
 * `ilike` with a `%…%` pattern, applied as its own term (NOT inside an `.or()`)
 * so a comma or a parenthesis in what the user typed can never be parsed as
 * PostgREST filter syntax. `%`, `_` and `\` are the three characters LIKE
 * treats as special, so they are backslash-escaped — Postgres' default escape
 * character — leaving a search for "50%" a search for a literal "50%" rather
 * than "50" followed by anything.
 *
 * Shared by getFilteredPosts and countPosts for the same reason
 * `applyStatusFilter` is: the list and the number above it must be counting the
 * same rows.
 */
function applySearchFilter<Q extends { ilike: (column: 'content', pattern: string) => Q }>(
  query: Q,
  search: string,
): Q {
  const escaped = search.trim().replace(/[\\%_]/g, (match) => `\\${match}`);
  return query.ilike('content', `%${escaped}%`);
}

/**
 * Authoring surfaces (composer, calendar, drafts, Home feed, reference drawer)
 * must NOT show ingested external posts (`source_kind='ingested_platform'`) —
 * those rows are published-outside-Mora posts pulled in ONLY to feed analytics
 * (top-posts / content performance). Every read path through PostService goes
 * through here so a status-less query can't silently leak them. Analytics RPCs
 * (`get_top_performing_posts`, `analytics_content_performance`) read ingested
 * posts DELIBERATELY and are intentionally not filtered. `source_kind` is NULL
 * for every Mora-authored post to date, so a bare `.neq` would drop them all —
 * NULL must pass.
 */
function excludeIngestedPosts<Q extends { or: (filter: string) => Q }>(query: Q): Q {
  return query.or('source_kind.is.null,source_kind.neq.ingested_platform');
}

/**
 * `social_posts` has NO `project_id` column — the project binding lives in
 * `campaign_id` (verified live against `information_schema`; the generated
 * types agree, listing `campaign_id` and `shopify_product_id` but no
 * `project_id`). Every `.eq('project_id', …)` and every `project_id:` insert
 * key in this file was a guaranteed 42703 the moment the value went non-null,
 * which is why `getPostsByProject()` has never returned a row.
 *
 * `projectId` and `campaignId` name the SAME binding, so they are collapsed
 * here rather than emitting two conflicting `.eq('campaign_id', …)` terms.
 */
function effectiveCampaignId(filters: {
  campaignId?: string;
  projectId?: string;
}): string | undefined {
  return filters.campaignId ?? filters.projectId;
}

/**
 * Result shape of the `approve_and_schedule` RPC (Phase A of the plan-apply
 * flow). See supabase/migrations/20260730120000_approve_and_schedule.sql.
 */
export interface AssignScheduleBatchResult {
  projectId: string;
  createdProject: boolean;
  assigned: Array<{ post_id: string; slot_id: string | null; scheduled_at: string }>;
  skipped: Array<{ post_id: string | null; reason: string }>;
}

export interface ScheduleAssignmentInput {
  postId: string;
  slotId?: string | null;
  scheduledAt: string;
}

/**
 * Local escape hatch for an RPC that is not in the generated types until
 * `npm run gen:types` runs after the migration lands. Mirrors the existing
 * pattern in BusinessHealthScoreService.ts.
 */
type UntypedRpc = {
  rpc: (
    fn: string,
    params: Record<string, unknown>,
  ) => PromiseLike<{ data: unknown; error: { message: string } | null }>;
};

/** Typed `error` codes the RPC returns, rendered as something a user can act on. */
const ASSIGN_SCHEDULE_ERRORS: Record<string, string> = {
  not_authenticated: 'Your session expired. Sign in again and retry.',
  invalid_assignments: 'Something went wrong preparing these dates. Try again.',
  batch_too_large: 'Too many posts at once — schedule 100 or fewer at a time.',
  project_not_found: "That project no longer exists, so we couldn't attach these posts.",
  project_create_failed: "We couldn't create a project to hold these posts.",
};

function applyPostFilters(query: any, filters: PostFilters, activeKitId: string | null): any {
  if (activeKitId) {
    query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
  }

  if (filters.platform) {
    query = query.contains('platforms', [filters.platform]);
  }

  if (filters.platforms?.length) {
    query = query.overlaps('platforms', filters.platforms);
  }

  if (filters.status) {
    query = applyStatusFilter(query, filters.status);
  }

  if (filters.uncleared) {
    query = query.is('attention_cleared_at', null);
  }

  if (filters.accountId) {
    query = query.eq('social_account_id', filters.accountId);
  }

  if (filters.competitorId) {
    query = query.eq('competitor_id', filters.competitorId);
  }

  if (filters.search?.trim()) {
    query = applySearchFilter(query, filters.search);
  }

  if (filters.startDate && filters.endDate) {
    const s = filters.startDate;
    const e = filters.endDate;
    query = query.or(
      [
        `and(scheduled_at.gte.${s},scheduled_at.lte.${e})`,
        `and(status.eq.published,published_at.gte.${s},published_at.lte.${e})`,
        ...PLATFORM_SCHEDULE_WINDOW_TERMS,
      ].join(','),
    );
  } else if (filters.startDate) {
    query = query.gte('scheduled_at', filters.startDate);
  } else if (filters.endDate) {
    query = query.lte('scheduled_at', filters.endDate);
  }

  if (filters.hasMedia !== undefined) {
    if (filters.hasMedia) {
      query = query.not('media_urls', 'is', null).gt('media_urls', '{}');
    } else {
      query = query.or('media_urls.is.null,media_urls.eq.{}');
    }
  }

  if (filters.productId) {
    query = query.eq('shopify_product_id', filters.productId);
  }

  const campaignFilterId = effectiveCampaignId(filters);
  if (campaignFilterId) {
    query = query.eq('campaign_id', campaignFilterId);
  }

  if (filters.collectionId) {
    query = query.eq('collection_id', filters.collectionId);
  }
  return query;
}

export class PostService extends BaseService {
  private static instance: PostService;

  private constructor() {
    super({
      defaultTTL: 5 * 60 * 1000, // 5 minutes
      enableRetry: true,
      maxRetries: 3,
    });
  }

  public static getInstance(): PostService {
    if (!PostService.instance) {
      PostService.instance = new PostService();
    }
    return PostService.instance;
  }

  /**
   * Shared-account RLS: social_posts rows must be owned by
   * current_account_owner_id(), not the raw session user — otherwise a team
   * member's insert violates the INSERT check. Reads rely on RLS filtering
   * automatically, but every INSERT needs the resolved owner id in its
   * `user_id` column (the same rule `approve_and_schedule` applies server-side).
   */
  /**
   * The workspace (brand kit) this user is currently acting in, or null.
   *
   * Copied verbatim from `CampaignService.resolveActiveBrandKitId` (services
   * are independent classes, not designed to import each other's private
   * helpers). Always via `get_effective_brand_kit()` — the zero-arg,
   * `authenticated` entry point that resolves through `auth.uid()`. Never
   * re-derive the rule here: it lives once, in
   * `resolve_effective_brand_kit_id()` in Postgres (this user's pointer ->
   * the account default -> most recent).
   *
   * The actor-taking variant (`get_effective_brand_kit_for`) is service_role
   * only and must never be reachable from a browser client.
   */
  private async resolveOwnerId(): Promise<string> {
    return this.ensureAuthenticatedOwner();
  }

  /**
   * Get all posts for the current user
   */
  /** Posts already saved from one article, so a reopened chat can show them. */
  async getPostsForArticle(
    articleId: string,
  ): Promise<ServiceResponse<Array<Pick<PostRow, 'id' | 'content' | 'platforms' | 'status'>>>> {
    return this.execute(async () => {
      const { data, error } = await this.client
        .from('social_posts')
        .select('id, content, platforms, status')
        .eq('source_article_id', articleId)
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return data ?? [];
    });
  }

  async getUserPosts(options?: QueryOptions): Promise<ServiceResponse<PostRow[]>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      // The workspace is part of the cache key: without it a read made in one workspace is
      // served from cache after a switch, and its drafts show up in the next one.
      const activeKitId = await this.resolveActiveBrandKitId();
      const cacheKey = `posts-${userId}-${activeKitId ?? 'none'}-${JSON.stringify(options)}`;
      return this.executeWithCache(
        cacheKey,
        async () => {
          let query = excludeIngestedPosts(this.client.from('social_posts').select('*')).order(
            'created_at',
            { ascending: false },
          );

          // Scope to the active workspace, keeping unattributed (legacy,
          // pre-workspace-stamping) rows visible — see resolveActiveBrandKitId.
          if (activeKitId) {
            query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
          }

          // Apply query options with default limit
          const queryOptions = {
            page: 0,
            limit: 100, // Default limit to prevent fetching too many posts
            ...options,
          };
          query = this.buildQuery(query, queryOptions);
          const { data, error } = await query;

          if (error) {
            throw error;
          }
          return data || [];
        },
        { ttl: 2 * 60 * 1000 }, // 2 minute cache for posts
      );
    }, 'Failed to get user posts');
  }

  /**
   * Get user posts with pagination
   */
  async getUserPostsPaginated(
    pageParam: number = 0,
    pageSize: number = 20,
    filters?: PostFilters,
    options?: QueryOptions,
  ): Promise<ServiceResponse<{ posts: PostRow[]; nextPage: number | null }>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      const from = pageParam * pageSize;
      const to = from + pageSize - 1;

      // The workspace is part of the cache key: without it a read made in one workspace is
      // served from cache after a switch, and its drafts show up in the next one.
      const activeKitId = await this.resolveActiveBrandKitId();
      const cacheKey = `posts-paginated-${userId}-${activeKitId ?? 'none'}-${pageParam}-${pageSize}-${JSON.stringify({ filters, options })}`;
      return this.executeWithCache(
        cacheKey,
        async () => {
          let query = excludeIngestedPosts(
            this.client.from('social_posts').select('*', { count: 'exact' }),
          );

          // Scope to the active workspace, keeping unattributed (legacy,
          // pre-workspace-stamping) rows visible — see resolveActiveBrandKitId.
          if (activeKitId) {
            query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
          }

          // Apply filters if provided
          if (filters) {
            if (filters.platform) {
              query = query.contains('platforms', [filters.platform]);
            }
            if (filters.platforms?.length) {
              query = query.overlaps('platforms', filters.platforms);
            }
            if (filters.status) {
              query = applyStatusFilter(query, filters.status);
            }
            if (filters.accountId) {
              query = query.eq('social_account_id', filters.accountId);
            }
            if (filters.startDate) {
              query = query.gte('scheduled_at', filters.startDate);
            }
            if (filters.endDate) {
              query = query.lte('scheduled_at', filters.endDate);
            }
            if (filters.hasMedia !== undefined) {
              if (filters.hasMedia) {
                query = query.not('media_urls', 'is', null).gt('media_urls', '{}');
              } else {
                query = query.or('media_urls.is.null,media_urls.eq.{}');
              }
            }
            if (filters.productId) {
              query = query.eq('shopify_product_id', filters.productId);
            }
            const campaignFilterId = effectiveCampaignId(filters);
            if (campaignFilterId) {
              query = query.eq('campaign_id', campaignFilterId);
            }
            if (filters.collectionId) {
              query = query.eq('collection_id', filters.collectionId);
            }
          }

          // Apply sorting. `id` is a required secondary tiebreaker: rows with
          // an identical `created_at` (e.g. bulk-seeded/backfilled posts) have
          // no other stable ordering, so offset pagination can return the same
          // row on two different pages (or skip one) without it.
          query = query.order('created_at', { ascending: false }).order('id', { ascending: false });

          // Apply query options
          query = this.buildQuery(query, options);

          // Apply pagination
          query = query.range(from, to);

          const { data, error, count } = await query;

          if (error) {
            throw error;
          }

          const nextPage = to < (count ?? 0) - 1 ? pageParam + 1 : null;
          const result = { posts: data || [], nextPage };

          return result;
        },
        { ttl: 2 * 60 * 1000 }, // 2 minute cache for posts
      );
    }, 'Failed to get user posts');
  }

  /**
   * Get only the post date fields needed for lightweight activity summaries.
   */
  async getRecentPostActivityDates(
    limit: number = 75,
  ): Promise<ServiceResponse<PostActivityDateRow[]>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const boundedLimit = Math.min(Math.max(limit, 1), 250);

      // The workspace is part of the cache key: without it a read made in one workspace is
      // served from cache after a switch, and its drafts show up in the next one.
      const activeKitId = await this.resolveActiveBrandKitId();
      const cacheKey = `post-activity-dates-${userId}-${activeKitId ?? 'none'}-${boundedLimit}`;
      return this.executeWithCache(
        cacheKey,
        async () => {
          let query = this.client
            .from('social_posts')
            .select('created_at,published_at,scheduled_at');

          // Scope to the active workspace, keeping unattributed (legacy,
          // pre-workspace-stamping) rows visible — see resolveActiveBrandKitId.
          if (activeKitId) {
            query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
          }

          query = query.order('created_at', { ascending: false }).limit(boundedLimit);

          const { data, error } = await query;

          if (error) {
            throw error;
          }

          return data || [];
        },
        { ttl: 2 * 60 * 1000 },
      );
    }, 'Failed to get recent post activity dates');
  }

  /**
   * Get filtered posts.
   *
   * `select` lets list surfaces pass a minimal column projection instead of
   * dragging every heavy JSON column (platform_response, platform_schedules,
   * metrics, media_keys) over the wire for rows that only render a title +
   * meta line. Defaults to '*' so every existing caller is unaffected. A
   * caller passing a narrower projection must only read the projected fields
   * from the returned rows (the return type stays PostRow[] — the cast below
   * preserves the full-row typing the '*' default provides).
   *
   * Server-side pagination: pass `options.page` + `options.limit` — the
   * shared `buildQuery` maps them to `.range(page*limit, page*limit+limit-1)`.
   */
  async getFilteredPosts(
    filters: PostFilters,
    options?: QueryOptions,
    select: string = '*',
  ): Promise<ServiceResponse<PostRow[]>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      // The workspace is part of the cache key: without it a read made in one workspace is
      // served from cache after a switch, and its drafts show up in the next one.
      const activeKitId = await this.resolveActiveBrandKitId();
      const cacheKey = `filtered-posts-${userId}-${activeKitId ?? 'none'}-${JSON.stringify({ filters, options, select })}`;
      return this.executeWithCache(
        cacheKey,
        async () => {
          let query = excludeIngestedPosts(this.client.from('social_posts').select(select as '*'));

          // Scope to the active workspace, keeping unattributed (legacy,
          // pre-workspace-stamping) rows visible — see resolveActiveBrandKitId.
          // This is the primary read path behind Board/Calendar/drafts/Insights,
          // so this is the filter that actually matters for the multi-workspace
          // bleed this method exists to fix.
          query = applyPostFilters(query, filters, activeKitId);

          // Apply query options with default limit
          const queryOptions = {
            page: 0,
            limit: 100, // Default limit to prevent fetching too many posts
            orderBy: 'scheduled_at',
            orderDirection: 'desc',
            ...options,
          };
          query = this.buildQuery(query, queryOptions);

          // Offset pagination needs a TOTAL order: bulk-created rows share
          // identical created_at/scheduled_at timestamps and Postgres gives
          // ties no stable order between queries, so adjacent `.range()`
          // windows can both duplicate and drop rows. A deterministic id
          // tiebreaker (appended after the primary order) makes every page
          // window disjoint; it only affects rows whose primary sort values
          // tie, where the order was previously arbitrary.
          if (queryOptions.orderBy !== 'id') {
            query = query.order('id', {
              ascending: queryOptions.orderDirection !== 'desc',
            });
          }

          const { data, error } = await query;
          if (error) throw error;

          return data || [];
        },
        { ttl: 2 * 60 * 1000 },
      );
    }, 'Failed to get filtered posts');
  }

  /**
   * Exact post count for the current user with the same filters as
   * getFilteredPosts, but using a head-only query so lightweight badges and
   * checklist widgets never download post payloads just to count them.
   */
  async countPosts(filters: PostFilters = {}): Promise<ServiceResponse<number>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      let query = excludeIngestedPosts(
        this.client.from('social_posts').select('id', { count: 'exact', head: true }),
      );

      const activeKitId = await this.resolveActiveBrandKitId();
      query = applyPostFilters(query, filters, activeKitId);

      const { count, error } = await query;
      if (error) throw error;
      return count ?? 0;
    }, 'Failed to count posts');
  }

  /**
   * IDs of the current user's posts in any of the given statuses — a light,
   * id-only payload for the needs-attention dismissal signature, which needs
   * post IDENTITY (which posts failed), not just a count. Bounded to 500;
   * failed posts are few by nature and the signature only needs set equality.
   *
   * `.order('updated_at', { ascending: false })` is load-bearing, not
   * cosmetic: past 500 lifetime failures, an unordered `.limit(500)` gives
   * Postgres no guarantee which 500 rows come back, and a plain scan tends to
   * stop as soon as it hits 500 matches — which, since a row's `updated_at`
   * moves forward when it transitions to 'failed', disproportionately
   * excludes the NEWEST failure rather than an old one. That silently
   * suppresses the un-dismiss signal for a genuinely new failure (see the
   * O-polish review packet, badge-count angle, P2). Ordering newest-first
   * makes the sample actually be "the newest 500 failures" as the caller's
   * doc comment already claims.
   */
  async getPostIdsByStatuses(statuses: string[]): Promise<ServiceResponse<string[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      let query = this.client
        .from('social_posts')
        .select('id')
        .in('status', statuses)
        .is('attention_cleared_at', null);

      // Scope to the active workspace, keeping unattributed (legacy,
      // pre-workspace-stamping) rows visible — see resolveActiveBrandKitId.
      // This powers an account-scoped attention signal (needs-attention
      // dismissal), so it must agree with the other list surfaces.
      const activeKitId = await this.resolveActiveBrandKitId();
      if (activeKitId) {
        query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
      }

      const { data, error } = await query.order('updated_at', { ascending: false }).limit(500);

      if (error) throw error;
      return (data ?? []).map((row) => row.id);
    }, 'Failed to get post ids by status');
  }

  /**
   * Get a single post by ID
   */
  async getPost(id: string): Promise<ServiceResponse<PostRow>> {
    // Don't cache individual post fetches to ensure fresh data
    return this.execute(async () => {
      await this.ensureAuthenticated();

      const { data, error } = await this.client
        .from('social_posts')
        .select('*')
        .eq('id', id)
        .single();

      if (error) {
        throw error;
      }

      if (!data) {
        throw ServiceError.notFound('Post');
      }
      return data;
    }, 'Failed to get post');
  }

  async getScheduledPostDates(excludePostId?: string): Promise<ServiceResponse<string[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      let query = this.client
        .from('social_posts')
        .select('id, scheduled_at')
        .eq('status', 'scheduled')
        .not('scheduled_at', 'is', null);

      // Scope to the active workspace, keeping unattributed (legacy,
      // pre-workspace-stamping) rows visible — see resolveActiveBrandKitId.
      // Feeds calendar conflict/free-slot detection, an account-scoped surface.
      const activeKitId = await this.resolveActiveBrandKitId();
      if (activeKitId) {
        query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
      }

      query = query.order('scheduled_at', { ascending: true }).limit(500);

      if (excludePostId) {
        query = query.neq('id', excludePostId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? [])
        .map((row) => row.scheduled_at)
        .filter((date): date is string => typeof date === 'string' && date.length > 0);
    }, 'Failed to get scheduled post dates');
  }

  /**
   * Get a post by its originating video_job_id. Returns `null` (not an error) if
   * no post has been attached yet — callers use this for "open the draft post
   * for this rendered video" lookups from the Files page.
   */
  async getByVideoJobId(
    jobId: string,
  ): Promise<ServiceResponse<{ id: string; campaign_id: string | null } | null>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('social_posts')
        .select('id, campaign_id')
        .eq('video_job_id', jobId)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    }, 'Failed to look up post by video job');
  }

  /**
   * Assign a post to a campaign (plan) without locking to a specific slot.
   * Used by SlotPickerDialog when entered without a planId and the chosen
   * plan has no open slots — preserves the loose "attach to plan" affordance
   * for users coming from /files (PD-2 Task 2).
   */
  async assignToCampaign(postId: string, campaignId: string): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { error } = await this.client
        .from('social_posts')
        .update({ campaign_id: campaignId })
        .eq('id', postId);
      if (error) throw error;
    }, 'Failed to assign post to campaign');
  }

  /**
   * Create a new post
   */
  async createPost(input: CreatePostInput): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const ownerId = await this.resolveOwnerId();
      const activeKitId = await this.resolveActiveBrandKitId();

      // Validate and sanitize content
      const sanitizeContent = (content: string): string => {
        // Remove any script tags and dangerous HTML
        return (content || '')
          .replace(/<script[^>]*>.*?<\/script>/gi, '')
          .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
          .trim();
      };

      const content = input.content || (input.scheduledAt ? 'New post' : '');
      const sanitized = sanitizeContent(content);

      const postPlatforms = input.platforms?.length
        ? input.platforms
        : input.platform
          ? [input.platform]
          : ['instagram'];
      const requestedScheduledAt = input.scheduledAt ?? null;

      const postData: PostInsert & {
        campaign_id?: string;
        source_article_id?: string | null;
        source_url?: string | null;
        format_hint?: string | null;
      } = {
        user_id: ownerId,
        authored_by: userId,
        content: sanitized,
        platforms: postPlatforms,
        // Stamp the active workspace at creation, same pattern as
        // CampaignService.createCampaign — new rows are attributable, while
        // pre-workspace-stamping rows stay NULL (legacy/account-wide).
        brand_kit_id: activeKitId,
        // All new rows begin as drafts. Scheduling is a state transition with
        // target materialization and cover-readiness checks; inserting a due
        // `scheduled` row here bypassed that safety boundary in bulk and
        // Workbench flows.
        status: 'draft',
        scheduled_at: null,
        media_urls: input.mediaUrls || [],
        shopify_product_id: input.productId,
        collection_id: input.collectionId,
        post_type: 'organic',
        ...(effectiveCampaignId(input) ? { campaign_id: effectiveCampaignId(input) } : {}),
        ...(input.mediaUrls?.length || (input.format && input.format !== 'feed')
          ? {
              media_keys: {
                ...(input.format && input.format !== 'feed' ? { format: input.format } : {}),
                ...(input.mediaUrls?.length
                  ? { childManifest: initialChildManifest(input.mediaUrls) }
                  : {}),
              },
            }
          : {}),
        ...(input.sourceArticleId ? { source_article_id: input.sourceArticleId } : {}),
        ...(input.sourceUrl ? { source_url: input.sourceUrl } : {}),
        ...(input.formatHint ? { format_hint: input.formatHint } : {}),
      };

      const { data, error } = await this.client
        .from('social_posts')
        .insert(postData)
        .select()
        .single();

      if (error) throw error;

      // Clear cache
      this.clearPostCaches(userId);

      if (requestedScheduledAt) {
        const scheduled = await this.schedulePost(data.id, requestedScheduledAt);
        if (scheduled.error || !scheduled.data) {
          throw new Error(
            scheduled.error?.message ??
              'The draft was saved, but its publishing destinations are not ready to schedule.',
          );
        }
        return scheduled.data;
      }

      return data;
    }, 'Failed to create post');
  }

  /**
   * Update an existing post
   *
   * `expectedUpdatedAt` (optional, threaded onto the existing `PostUpdate`
   * input type rather than a separate params object): pass the `updated_at`
   * the caller last read to get an optimistic-concurrency guard on the write
   * — mirrors the class of problem `mergeMediaKeysEntry` already solves for
   * `media_keys`, extended to the caption/content/media_urls fields that
   * write straight through this method. Omit it (default, every existing
   * caller) and behavior is byte-for-byte unchanged: a plain `.eq('id', id)`
   * update, last-write-wins. When supplied, the update is additionally
   * gated on `.eq('updated_at', expectedUpdatedAt)`; if another session
   * already saved in between, zero rows match and `.single()` raises
   * PostgREST's `PGRST116` ("no rows returned") — which we translate into a
   * `ServiceError.conflict()` (`code: 'CONFLICT'`) instead of a generic
   * database error, so the caller can distinguish "someone else edited this
   * post" from a real failure and re-prompt rather than silently clobber.
   */
  async updatePost(
    id: string,
    updates: Partial<PostUpdate> & { expectedUpdatedAt?: string },
  ): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const { expectedUpdatedAt, ...updateFields } = updates;

      // Remove undefined values and prepare updates
      const cleanedUpdates = Object.entries(updateFields).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key as keyof PostUpdate] = value;
        }
        return acc;
      }, {} as PostUpdate);

      // Sanitize content if provided
      if (cleanedUpdates.content) {
        cleanedUpdates.content = cleanedUpdates.content
          .replace(/<script[^>]*>.*?<\/script>/gi, '')
          .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
          .trim();
      }

      // BI-5-03 pre-flight (Path 1 client-direct backstop): browser→Supabase
      // writes have no app server in the loop, so this is UX-only — the DB
      // trigger (social_posts BEFORE UPDATE) is the authoritative stop.
      // Reads ONLY the INHERITED (ancestor-only) enforcement set — a
      // corporate account editing its own locked values is never blocked.
      const touchesCaption = 'content' in cleanedUpdates || 'platform_captions' in cleanedUpdates;
      const touchesMedia = 'media_urls' in cleanedUpdates;
      if (touchesCaption || touchesMedia) {
        const { inheritedLockedFields, lockedValues } = await getEffectiveLockedFields(this.client);

        if (inheritedLockedFields.length > 0 && touchesCaption) {
          const proposedCaption =
            typeof cleanedUpdates.content === 'string'
              ? cleanedUpdates.content
              : typeof cleanedUpdates.platform_captions === 'object' &&
                  cleanedUpdates.platform_captions !== null
                ? Object.values(cleanedUpdates.platform_captions as Record<string, unknown>)
                    .filter((v): v is string => typeof v === 'string')
                    .join('\n')
                : undefined;
          const guard = assertEditAllowed({
            lockedFields: inheritedLockedFields,
            field: 'caption',
            intent: {
              kind: 'caption-rewrite',
              proposedCaption,
              disclaimerText: lockedValues.disclaimerText ?? null,
            },
          });
          if (!guard.allowed) {
            throw new ServiceError('edit_not_allowed', guard.reason, {
              lockedField: guard.lockedField,
            });
          }
        }

        // Logo-slide swap detection (BI-5-01 contract): diff old vs new
        // media_urls; a removed/replaced entry equal to the locked corporate
        // logo URL targets the logo slide → 'logo-swap' (denied under a
        // logoUrl lock). Every other swap is 'manual-media-swap' and stays
        // allowed — media is not a monolith (RESEARCH Pitfall 2).
        if (inheritedLockedFields.length > 0 && touchesMedia) {
          const nextUrls = Array.isArray(cleanedUpdates.media_urls)
            ? (cleanedUpdates.media_urls as string[])
            : [];
          const { data: currentRow } = await this.client
            .from('social_posts')
            .select('media_urls')
            .eq('id', id)
            .maybeSingle();
          const oldUrls = (currentRow?.media_urls ?? []) as string[];
          const lockedLogoUrl = lockedValues.logoUrl;
          const removedOrReplaced = oldUrls.filter((u) => !nextUrls.includes(u));
          const isLogoSlideSwap =
            !!lockedLogoUrl && removedOrReplaced.some((u) => u === lockedLogoUrl);
          const guard = assertEditAllowed({
            lockedFields: inheritedLockedFields,
            field: 'media',
            intent: { kind: isLogoSlideSwap ? 'logo-swap' : 'manual-media-swap' },
          });
          if (!guard.allowed) {
            throw new ServiceError('edit_not_allowed', guard.reason, {
              lockedField: guard.lockedField,
            });
          }
        }
      }

      let updateQuery = this.client.from('social_posts').update(cleanedUpdates).eq('id', id);
      if (expectedUpdatedAt) {
        updateQuery = updateQuery.eq('updated_at', expectedUpdatedAt);
      }
      const { data, error } = await updateQuery.select().single();

      if (error) {
        // With the optimistic-concurrency guard active, a `.single()` "no
        // rows" result means the `updated_at` we gated on no longer matches
        // — someone else saved this post between our read and our write —
        // not a real database failure. Without expectedUpdatedAt this branch
        // is unreachable and behavior is unchanged from before this guard.
        if (expectedUpdatedAt && error.code === 'PGRST116') {
          throw ServiceError.conflict(
            'This post was edited elsewhere before your changes were saved.',
            { postId: id, reason: 'stale_updated_at' },
          );
        }
        throw error;
      }
      if (!data) throw ServiceError.notFound('Post');

      const targetAuthorityFields = [
        'content',
        'platform_captions',
        'platforms',
        'media_keys',
        'media_urls',
        'media_type',
        'cover_image_url',
        'video_duration_seconds',
        'video_aspect_ratio',
        'title',
        // The DB trigger `social_posts_invalidate_targets_on_provider_input_change`
        // already watches this column and clears `target_first_ready` when it
        // changes on a draft/scheduled post. It was missing from THIS list, so
        // nothing re-materialized afterwards — changing which account a
        // SCHEDULED post publishes as left `target_first_ready = false` with
        // targets still pointing at the old connection, and the cron then
        // quarantined it to `failed` at its scheduled time. The post did not go
        // to the wrong account; it silently did not go at all, while the UI
        // showed the change as saved.
        'destination_connection_ids',
      ] as const;
      const refreshesTargetAuthority = targetAuthorityFields.some(
        (field) => field in cleanedUpdates,
      );
      let result = data;
      if (refreshesTargetAuthority && ['draft', 'scheduled'].includes(data.status)) {
        // The database trigger invalidates target_first_ready on any provider
        // input change. Reconcile immediately for normal browser edits so a
        // scheduled post cannot retain a stale immutable caption, platform,
        // placement, or media snapshot.
        const materialized = await targetCoverService.materializePostTargets(id);
        if (!materialized.error) {
          const { data: refreshed } = await this.client
            .from('social_posts')
            .select()
            .eq('id', id)
            .single();
          if (refreshed) result = refreshed;
        }
      }

      // Clear cache
      this.clearPostCaches(userId);

      return result;
    }, 'Failed to update post');
  }

  /**
   * Task A4: atomic per-platform merge via the `merge_post_media_keys` RPC
   * (migration `20260710250000_merge_post_media_keys_rpc.sql`, SECURITY
   * INVOKER — ownership enforced by the same `social_posts_owner_all` RLS
   * policy every other write already relies on, no hand-rolled auth check
   * needed here). Deep-merges `patch` into
   * `media_keys.perPlatform[platform]` server-side — a JSON `null` value on
   * any key DELETES it, mirroring `mediaKeysWriter.ts`'s
   * `entryPatchToJsonPatch` — so it can't be clobbered by a concurrent
   * session's read-then-write the way the legacy client-computed
   * whole-object update could.
   *
   * Deliberately NOT wrapped in `this.execute()`: the raw Postgrest error
   * (with its `.code`) is exactly what `mediaKeysCommitQueue.ts`'s
   * `mergeEntryViaRpc` feature-detection needs to distinguish "RPC not
   * deployed to this environment yet" — PostgREST's schema-cache miss
   * (`PGRST202`, the shape actually returned when an RPC hasn't been added
   * to the schema cache yet) or the raw Postgres SQLSTATE (`42883`,
   * undefined_function) — fall back to the legacy path — from a real write
   * failure; `execute()`'s `ServiceError` wrapping would erase the code.
   * `fn as never` / `args as never` mirrors `TemplateVersioningService.rpc()`
   * — the generated Supabase types don't know about this RPC until the next
   * `npm run gen:types` after the migration is applied.
   */
  async mergeMediaKeysEntry(
    postId: string,
    platform: string,
    patch: Record<string, unknown>,
  ): Promise<{
    data: PostMediaKeys | null;
    error: { code?: string; message: string } | null;
  }> {
    const { data, error } = (await this.client.rpc(
      'merge_post_media_keys' as never,
      {
        post_id: postId,
        platform,
        patch,
      } as never,
    )) as unknown as {
      data: PostMediaKeys | null;
      error: { code?: string; message: string } | null;
    };
    if (!error) {
      // `merge_post_media_keys` is the other normal writer for placement and
      // per-platform provider inputs. Refresh the immutable target snapshot
      // after its atomic merge; the database trigger remains the fail-closed
      // backstop if this request is interrupted.
      await targetCoverService.materializePostTargets(postId);
    }
    return { data: data ?? null, error: error ?? null };
  }

  /**
   * Exact post count for a status — head-only query, no row payload. Powers
   * honest counts where the list itself is page-capped (e.g. the Drafts
   * drawer fetches 100 but the badge must say 2,344 — F-092, UAT 2026-06-13).
   */
  async countPostsByStatus(status: string): Promise<ServiceResponse<number>> {
    return this.countPosts({ status });
  }

  /**
   * How many of this account's posts are sitting in the consumer trust gate.
   *
   * A held post keeps its `scheduled_at`, so it renders on the calendar as if
   * it were going out — the trigger deliberately does not clear the date, since
   * the owner's later approve should keep it. Without a durable count the held
   * state disappears on refresh and the user is left with dated posts that
   * silently never publish. That is exactly this repo's #1 bug class, so the
   * count exists to make the state survive a remount.
   *
   * Scope it to a project whenever the caller is showing the count next to that
   * project. Unscoped, a plan the user has never applied still announced "3 held
   * for approval" about posts somewhere else in the account.
   */
  async countHeldForReview(projectId?: string | null): Promise<ServiceResponse<number>> {
    return this.countPosts({
      status: 'pending_review',
      ...(projectId ? { projectId } : {}),
    });
  }

  /**
   * Phase A of the plan-apply flow: date a batch of drafts and bind them to a
   * project in ONE atomic RPC (`approve_and_schedule`).
   *
   * Deliberately does NOT change `status`. The RPC leaves every row a `draft`,
   * so it creates zero delivery authority and the consumer trust gate is not
   * bypassed — arming for delivery stays with `schedulePost()` below, which
   * routes through the `schedule-post` edge function that owns UTM stamping,
   * exact-target materialization and platform readiness.
   *
   * Pass `projectId: null` to bind to the account's single reusable
   * "Always-on" project, creating it on first use.
   */
  async assignScheduleBatch(
    assignments: ScheduleAssignmentInput[],
    projectId: string | null,
    projectName?: string | null,
  ): Promise<ServiceResponse<AssignScheduleBatchResult>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      if (assignments.length === 0) {
        throw ServiceError.validationError('No posts selected to schedule.');
      }

      const client = this.client as unknown as UntypedRpc;
      const { data, error } = await client.rpc('approve_and_schedule', {
        p_assignments: assignments.map((a) => ({
          post_id: a.postId,
          slot_id: a.slotId ?? null,
          scheduled_at: a.scheduledAt,
        })),
        p_project_id: projectId,
        p_project_name: projectName ?? null,
      });

      if (error) {
        throw this.createError(
          `Could not put these posts on the calendar: ${error.message}`,
          'QUERY_ERROR',
          error,
        );
      }

      const payload = (data ?? {}) as {
        error?: string;
        project_id?: string;
        created_project?: boolean;
        assigned?: AssignScheduleBatchResult['assigned'];
        skipped?: AssignScheduleBatchResult['skipped'];
      };

      if (payload.error || !payload.project_id) {
        throw this.createError(
          ASSIGN_SCHEDULE_ERRORS[payload.error ?? ''] ??
            'Could not put these posts on the calendar.',
          'QUERY_ERROR',
        );
      }

      this.clearPostCaches(userId);

      return {
        projectId: payload.project_id,
        createdProject: Boolean(payload.created_project),
        assigned: payload.assigned ?? [],
        skipped: payload.skipped ?? [],
      };
    }, 'Failed to put posts on the calendar');
  }

  /**
   * Persist studio review approval for one or more posts (F-073). Approval is
   * a nullable timestamp orthogonal to the publish lifecycle — an approved
   * post can still be a draft, and `approve_and_schedule` itself only does
   * `approved_at = coalesce(approved_at, now())`. This method therefore stays
   * the flag-only path on purpose; it is NOT routed through the scheduling RPC,
   * because most drafts legitimately belong to no project (1,542 of 1,628 on
   * prod, 2026-07-31) and inventing a date for them would be worse than
   * leaving them undated.
   *
   * It returns THE IDS IT ACTUALLY WROTE, which is the part that used to be
   * missing. `.in('id', ids)` silently matches nothing when the caller passes
   * ids that never became `social_posts` rows — a very common case, because the
   * chat surface tracks agent tool outputs whose ids are not post ids. The old
   * signature was `void`, so "approved 12 posts" was printed after writing zero
   * rows and nobody could tell. Callers must report against the returned array.
   *
   * The update is also explicitly scoped to `user_id = current_account_owner_id()`
   * rather than relying on RLS alone, matching every other write in this class
   * (THE IDENTITY INVARIANT — `social_posts` is account-scoped).
   */
  async setApproval(ids: string[], approved: boolean): Promise<ServiceResponse<string[]>> {
    return this.execute(async () => {
      if (ids.length === 0) return [];
      const userId = await this.ensureAuthenticated();
      const ownerId = await this.resolveOwnerId();
      const { data, error } = await this.client
        .from('social_posts')
        .update({ approved_at: approved ? new Date().toISOString() : null })
        .eq('user_id', ownerId)
        .in('id', ids)
        .select('id');
      if (error) throw error;
      this.clearPostCaches(userId);
      return (data ?? []).map((row) => row.id);
    }, 'Failed to save approval');
  }

  /**
   * Which of these ids are REALLY this account's posts, and what plan each one
   * belongs to. One round trip, and the only honest answer to a question the
   * approve path used to guess at from client state.
   *
   * Two guesses it replaces:
   *
   *  1. "Are these post ids?" The chat surface tracks AGENT TOOL OUTPUTS, and a
   *     tool-output id is frequently not a `social_posts` id. Every `.in('id',
   *     ids)` write therefore silently matched zero rows while the UI said it
   *     approved N. Rows returned here are proof of existence; ids absent from
   *     the result never existed and must be reported as such.
   *
   *  2. "Which project?" The client used to read a plan id out of a react-query
   *     /zustand snapshot that hydrates asynchronously, so a click landing
   *     before hydration saw `null` and silently took a different, weaker code
   *     path. `social_posts.campaign_id` is stamped SERVER-SIDE by the
   *     `bind_social_post_to_plan` trigger, so the post itself already knows
   *     its project with no client state in the loop and no waiting.
   *
   * Scoped to `user_id = current_account_owner_id()` like every other read here
   * (THE IDENTITY INVARIANT — `social_posts` is account-scoped), so a member
   * resolves the owner's rows rather than an empty set.
   */
  async resolvePostBindings(ids: string[]): Promise<
    ServiceResponse<
      Array<{
        id: string;
        campaignId: string | null;
        planSlotId: string | null;
        status: string | null;
        approvedAt: string | null;
      }>
    >
  > {
    return this.execute(async () => {
      if (ids.length === 0) return [];
      await this.ensureAuthenticated();
      const ownerId = await this.resolveOwnerId();
      const { data, error } = await this.client
        .from('social_posts')
        .select('id, campaign_id, plan_slot_id, status, approved_at')
        .eq('user_id', ownerId)
        .in('id', ids);
      if (error) throw error;
      return (data ?? []).map((row) => ({
        id: row.id,
        campaignId: (row.campaign_id as string | null) ?? null,
        planSlotId: (row.plan_slot_id as string | null) ?? null,
        status: (row.status as string | null) ?? null,
        approvedAt: (row.approved_at as string | null) ?? null,
      }));
    }, 'Failed to look up these posts');
  }

  /**
   * The project holding the posts a given conversation produced.
   *
   * 🔴 THIS EXISTS BECAUSE THE CHAT'S POST IDS ARE NOT POST IDS.
   * `normalizePosts` (src/lib/ai-agent/utils.ts) does
   * `id: post.id || crypto.randomUUID()`, so whenever the generation response
   * omits an id the client MINTS one. It is shaped like a real uuid, renders
   * like a real post, and matches zero rows forever after. Verified live on
   * 2026-07-31: session ad41b661 returned four ids from `generate_campaign_posts`
   * (c356c533, 94e8c64e, 8cad85d2, 7afda72b) and NONE of the four exists in
   * `social_posts`, while the four real rows for that project sat there under
   * completely different ids. So the approve path cannot use the on-screen ids
   * to find the plan; it has to ask from a direction the mint cannot corrupt.
   *
   * `social_posts.origin_session_id` is that direction. It is stamped
   * SERVER-SIDE at insert with the conversation that produced the row, and
   * `campaign_id` is stamped by the `bind_social_post_to_plan` trigger, so one
   * query over the posts themselves answers "which plan is this chat looking
   * at" with no client state, no hydration wait, and no id round-tripping.
   *
   * Returns the most recently written project among this session's posts —
   * a conversation that re-planned mid-thread has posts under more than one
   * project, and the current one is the one the user is looking at.
   */
  async resolveProjectForSession(sessionId: string): Promise<ServiceResponse<string | null>> {
    return this.execute(async () => {
      if (!sessionId) return null;
      await this.ensureAuthenticated();
      const ownerId = await this.resolveOwnerId();
      const { data, error } = await this.client
        .from('social_posts')
        .select('campaign_id, created_at')
        .eq('user_id', ownerId)
        .eq('origin_session_id', sessionId)
        .not('campaign_id', 'is', null)
        .order('created_at', { ascending: false })
        .limit(1);
      if (error) throw error;
      return ((data ?? [])[0]?.campaign_id as string | null) ?? null;
    }, 'Failed to find the plan for this conversation');
  }

  /**
   * The other posts one conversation produced, for "Next in this set" after a
   * post goes out. Same direction as `resolveProjectForSession`: the
   * server-stamped `origin_session_id`, never on-screen ids. Unpublished
   * posts only, soonest first; undated drafts last.
   */
  async getSessionSiblings(
    sessionId: string,
    excludePostId: string,
  ): Promise<
    ServiceResponse<
      Array<{
        id: string;
        status: string;
        scheduled_at: string | null;
        created_at: string;
        content: string | null;
      }>
    >
  > {
    return this.execute(async () => {
      if (!sessionId) return [];
      await this.ensureAuthenticated();
      const ownerId = await this.resolveOwnerId();
      const { data, error } = await this.client
        .from('social_posts')
        .select('id, status, scheduled_at, created_at, content')
        .eq('user_id', ownerId)
        .eq('origin_session_id', sessionId)
        .neq('id', excludePostId)
        .in('status', ['draft', 'scheduled'])
        .order('scheduled_at', { ascending: true, nullsFirst: false })
        .order('created_at', { ascending: true })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        status: string;
        scheduled_at: string | null;
        created_at: string;
        content: string | null;
      }>;
    }, 'Failed to load the other posts from this conversation');
  }

  /**
   * Where each post stands right now (draft, scheduled for when, published
   * when) — so a chat turn's post cards and summary say what is on the
   * calendar instead of only what was generated.
   */
  async getDeliveryStates(ids: string[]): Promise<
    ServiceResponse<
      Array<{
        id: string;
        status: string;
        scheduled_at: string | null;
        published_at: string | null;
        approved_at: string | null;
        media_urls: string[] | null;
      }>
    >
  > {
    return this.execute(async () => {
      if (ids.length === 0) return [];
      await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('social_posts')
        .select('id, status, scheduled_at, published_at, approved_at, media_urls')
        .in('id', ids);
      if (error) throw error;
      return (data ?? []) as Array<{
        id: string;
        status: string;
        scheduled_at: string | null;
        published_at: string | null;
        approved_at: string | null;
        media_urls: string[] | null;
      }>;
    }, 'Failed to load post status');
  }

  /**
   * Approval state for a set of post ids — used to rehydrate the studio's
   * isApproved flags after a refresh (F-073). Returns only the ids that are
   * approved.
   */
  async getApprovedIds(ids: string[]): Promise<ServiceResponse<string[]>> {
    return this.execute(async () => {
      if (ids.length === 0) return [];
      await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('social_posts')
        .select('id')
        .in('id', ids)
        .not('approved_at', 'is', null);
      if (error) throw error;
      return (data ?? []).map((r) => r.id);
    }, 'Failed to load approvals');
  }

  /**
   * Delete a post
   */
  async deletePost(id: string): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      if (isPreviewAccountFlagged()) {
        throw new ServiceError('PREVIEW_ACCOUNT', PREVIEW_ACCOUNT_MESSAGE);
      }
      const userId = await this.ensureAuthenticated();

      // F-137: capture the bound project slot BEFORE deleting so we can release
      // it afterward. The FK lives on the social_posts side, so a raw delete
      // leaves project_tasks.artifact_id dangling and the slot stuck in a
      // non-'todo' state (the project shows a filled-but-broken slot forever).
      const { data: existing } = await this.client
        .from('social_posts')
        .select('plan_slot_id')
        .eq('id', id)
        .maybeSingle();

      // .select('id') so a 0-row RLS-filtered delete is distinguishable from a
      // real delete -- Postgres/PostgREST return no error for a DELETE that
      // matches zero rows, so without this a caller has no way to tell "row
      // deleted" from "RLS silently blocked it" (both looked like success).
      const { data: deleted, error } = await this.client
        .from('social_posts')
        .delete()
        .eq('id', id)
        .select('id');

      if (error) throw error;
      if (!deleted || deleted.length === 0) {
        throw this.createError('Post not found or not permitted', 'FORBIDDEN');
      }

      // Reset the freed slot back to 'todo' (mirrors
      // PlanSlotService.updateAssignedPost(slotId, null) — both-or-neither
      // artifact_id/artifact_kind per the polymorphic trigger). Best-effort:
      // a slot-reset failure must never surface as a delete failure.
      const planSlotId = existing?.plan_slot_id ?? null;
      if (planSlotId) {
        const { error: slotErr } = await this.client
          .from('project_tasks')
          .update({ artifact_id: null, artifact_kind: null, status: 'todo' })
          .eq('id', planSlotId);
        if (slotErr) logger.error('deletePost: failed to reset freed project slot', slotErr);
      }

      // Clear cache
      this.clearPostCaches(userId);
    }, 'Failed to delete post');
  }

  async getDraftPostIds(filters: DraftIdFilters = {}): Promise<ServiceResponse<string[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();

      let query = this.client.from('social_posts').select('id').eq('status', 'draft');

      // Scope to the active workspace, keeping unattributed (legacy,
      // pre-workspace-stamping) rows visible — see resolveActiveBrandKitId.
      const activeKitId = await this.resolveActiveBrandKitId();
      if (activeKitId) {
        query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
      }

      query = query.order('created_at', { ascending: false }).limit(filters.limit ?? 2000);

      if (filters.platforms?.length) {
        query = query.overlaps('platforms', filters.platforms);
      }

      if (filters.createdAfter) {
        query = query.gte('created_at', filters.createdAfter);
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data ?? []).map((row) => row.id);
    }, 'Failed to load draft ids');
  }

  async bulkDeletePosts(ids: string[]): Promise<ServiceResponse<number>> {
    return this.execute(async () => {
      if (isPreviewAccountFlagged()) {
        throw new ServiceError('PREVIEW_ACCOUNT', PREVIEW_ACCOUNT_MESSAGE);
      }
      const userId = await this.ensureAuthenticated();
      const uniqueIds = Array.from(new Set(ids)).filter(Boolean);
      if (uniqueIds.length === 0) return 0;

      const chunkSize = 100;
      let deletedCount = 0;

      for (let i = 0; i < uniqueIds.length; i += chunkSize) {
        const chunk = uniqueIds.slice(i, i + chunkSize);
        const { data: existing, error: readError } = await this.client
          .from('social_posts')
          .select('id, plan_slot_id')
          .eq('status', 'draft')
          .in('id', chunk);

        if (readError) throw readError;

        const existingIds = (existing ?? []).map((row) => row.id);
        if (existingIds.length === 0) continue;

        const { error } = await this.client
          .from('social_posts')
          .delete()
          .eq('status', 'draft')
          .in('id', existingIds);

        if (error) throw error;
        deletedCount += existingIds.length;

        const slotIds = (existing ?? [])
          .map((row) => row.plan_slot_id)
          .filter((id): id is string => Boolean(id));

        if (slotIds.length > 0) {
          const { error: slotErr } = await this.client
            .from('project_tasks')
            .update({ artifact_id: null, artifact_kind: null, status: 'todo' })
            .in('id', slotIds);
          if (slotErr)
            logger.error('bulkDeletePosts: failed to reset freed project slots', slotErr);
        }
      }

      this.clearPostCaches(userId);
      return deletedCount;
    }, 'Failed to delete posts');
  }

  /**
   * Duplicate a post
   */
  async duplicatePost(id: string): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      // Get the original post
      const { data: original, error: fetchError } = await this.client
        .from('social_posts')
        .select('*')
        .eq('id', id)
        .single();

      if (fetchError) throw fetchError;
      if (!original) throw ServiceError.notFound('Post');

      // Create a copy without system fields. F-137: also strip the project
      // linkage + lifecycle fields so the copy is a fresh, UNBOUND draft.
      // Carrying plan_slot_id made two posts point at one project slot —
      // useSlotPosts is last-write-wins, so the duplicate silently overwrote
      // the original's slot preview in the workbench. campaign_id is kept on
      // purpose: the copy stays an unbound post under the same project
      // (surfaced via useUnboundPlanPosts). idempotency_key/approved_at are
      // dropped so the copy gets a clean key and no stale approval timestamp.
      // platform_schedules is stripped too: it carries live per-platform
      // lifecycle (platform_post_id/published_at) that must never ride along to
      // a fresh draft, which starts as one shared time.
      const {
        id: _,
        created_at: _created_at,
        updated_at: _updated_at,
        published_at: _published_at,
        platform_post_id: _platform_post_id,
        platform_response: _platform_response,
        platform_schedules: _platform_schedules,
        plan_slot_id: _plan_slot_id,
        idempotency_key: _idempotency_key,
        approved_at: _approved_at,
        ...postData
      } = original;

      const duplicate: PostInsert = {
        ...postData,
        authored_by: userId, // The duplicating user is the author of the copy, not the original author
        status: 'draft', // Reset to draft
        scheduled_at: null, // Clear schedule
      };

      const { data, error } = await this.client
        .from('social_posts')
        .insert(duplicate)
        .select()
        .single();

      if (error) throw error;

      // Clear cache
      this.clearPostCaches(userId);

      return data;
    }, 'Failed to duplicate post');
  }

  /**
   * Get scheduled posts for a date range
   */
  async getScheduledPosts(
    startDate: string,
    endDate: string,
    options?: QueryOptions,
  ): Promise<ServiceResponse<PostRow[]>> {
    return this.getFilteredPosts(
      {
        status: 'scheduled',
        startDate,
        endDate,
      },
      options,
    );
  }

  /**
   * Get draft posts
   */
  async getDraftPosts(options?: QueryOptions): Promise<ServiceResponse<PostRow[]>> {
    return this.getFilteredPosts({ status: 'draft' }, options);
  }

  /**
   * The LinkedIn agent's drafts, oldest first, across the whole account: the agent speaks for a person, not a
   * workspace, so the active brand kit does not narrow it. Approved drafts are included; the deck decides which
   * still need a decision.
   */
  async getLinkedInAgentDrafts(): Promise<
    ServiceResponse<
      Pick<
        PostRow,
        | 'id'
        | 'content'
        | 'quality'
        | 'approved_at'
        | 'source_url'
        | 'created_at'
        | 'scheduled_at'
        | 'brand_kit_id'
        | 'status'
      >[]
    >
  > {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const ownerId = await this.resolveOwnerId();
      const { data, error } = await this.client
        .from('social_posts')
        .select(
          'id, content, quality, approved_at, source_url, created_at, scheduled_at, brand_kit_id, status',
        )
        .eq('user_id', ownerId)
        .eq('source_kind', 'linkedin_agent')
        // Scheduled posts stay visible in the deck's Ready list, where the person can stop them.
        .in('status', ['draft', 'scheduled'])
        // Drafts written in one run share a timestamp; the id keeps the deck from reordering under the reader.
        .order('created_at', { ascending: true })
        .order('id', { ascending: true })
        .limit(50);
      if (error) throw error;
      return data ?? [];
    }, 'Failed to load LinkedIn drafts');
  }

  /**
   * Approve or un-approve one LinkedIn agent draft, writing `approved_at` and the review record in `quality`
   * together. A review record, never a release: nothing here schedules or publishes (ruling 2).
   */
  async setLinkedInAgentReview(
    id: string,
    review: { approvedAt: string | null; quality: PostRow['quality'] },
  ): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const ownerId = await this.resolveOwnerId();
      const { data, error } = await this.client
        .from('social_posts')
        .update({ approved_at: review.approvedAt, quality: review.quality })
        .eq('user_id', ownerId)
        .eq('id', id)
        .eq('source_kind', 'linkedin_agent')
        .select('id');
      if (error) throw error;
      if (!data?.length) throw new Error('That draft is no longer here.');
      this.clearPostCaches(userId);
    }, 'Failed to save the review');
  }

  /** Record the views a person read off LinkedIn for a published agent post. */
  async setLinkedInAgentResults(
    id: string,
    quality: PostRow['quality'],
  ): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const ownerId = await this.resolveOwnerId();
      const { data, error } = await this.client
        .from('social_posts')
        .update({ quality })
        .eq('user_id', ownerId)
        .eq('id', id)
        .eq('source_kind', 'linkedin_agent')
        .select('id');
      if (error) throw error;
      if (!data?.length) throw new Error('That post is no longer here.');
      this.clearPostCaches(userId);
    }, 'Failed to save the views');
  }

  /**
   * Get published posts
   */
  async getPublishedPosts(options?: QueryOptions): Promise<ServiceResponse<PostRow[]>> {
    return this.getFilteredPosts({ status: 'published' }, options);
  }

  /**
   * Place a draft on the calendar without making it publish-ready.
   */
  async planDraftOnCalendar(
    id: string,
    scheduledAt: string,
    options?: PlanDraftOnCalendarOptions,
  ): Promise<ServiceResponse<PostRow>> {
    const destination = options?.destination;
    return this.updatePost(id, {
      status: 'draft',
      scheduled_at: scheduledAt,
      ...(options?.collectionId !== undefined ? { collection_id: options.collectionId } : {}),
      ...(options?.campaignId !== undefined ? { campaign_id: options.campaignId } : {}),
      ...(destination
        ? {
            destination_connection_ids: { [destination.platform]: destination.connectionId },
            destination_resource_ids: { [destination.platform]: destination.resourceId },
          }
        : {}),
    });
  }

  /**
   * Schedule a post
   */
  async schedulePost(
    id: string,
    scheduledAt: string,
    options: { onSkipped?: (partition: SchedulePartition) => void } = {},
  ): Promise<ServiceResponse<PostRow>> {
    const pausedState: { postId?: string; platforms: string[] } = { platforms: [] };
    const result = await this.execute(async () => {
      if (isPreviewAccountFlagged()) {
        throw new ServiceError('PREVIEW_ACCOUNT', PREVIEW_ACCOUNT_MESSAGE);
      }
      const { row, enabled } = await this.loadScheduleContext(id);
      const connected = await this.loadConnectedPlatforms(row.user_id, enabled);
      // A LinkedIn post with a profile and a Company Page both selected must
      // be told which one it publishes as. Never default: lead the person to
      // the picker, and let the same action run again once it is saved.
      if (
        enabled.includes('linkedin') &&
        (await linkedInDestinationChoiceMissing(this.client, row))
      ) {
        requestLinkedInDestinationChoice({
          postId: id,
          actionLabel: 'Schedule',
          retry: async () =>
            (await this.schedulePost(id, scheduledAt, options)).error?.message ?? null,
        });
        throw new ServiceError(
          LINKEDIN_DESTINATION_REQUIRED,
          LINKEDIN_DESTINATION_REQUIRED_MESSAGE,
        );
      }
      let platforms = enabled;
      if (options.onSkipped) {
        const partition = this.partitionScheduleReadiness(row, enabled, { connected });
        if (partition.ready.length === 0) {
          throw ServiceError.validationError(
            partition.skipped[0]?.reason ?? 'No platform is ready to schedule.',
            { skipped: partition.skipped },
          );
        }
        platforms = partition.ready;
        if (partition.skipped.length > 0) {
          const skippedPlatforms = partition.skipped.map((entry) => entry.platform);
          await this.pauseSkippedPlatforms(row, skippedPlatforms);
          pausedState.postId = row.id;
          pausedState.platforms = skippedPlatforms;
          options.onSkipped(partition);
        }
      } else {
        this.assertScheduleReadiness(row, enabled, { connected });
      }

      // Scheduling changes durable delivery state. It must therefore use the
      // same governed server transition as post-now: the edge function owns
      // organization review, exact-account ambiguity, target materialization,
      // and the status flip. The former browser path updated social_posts and
      // then *best-effort* materialized targets, which could leave a scheduled
      // row whose immutable dispatch authority was stale or absent.
      const { data: scheduleResult, error: scheduleError } = await this.client.functions.invoke<{
        socialPostId?: string;
        status?: string;
      }>('schedule-post', {
        body: {
          postId: id,
          platforms,
          content: row.content ?? '',
          mediaUrls: row.media_urls ?? [],
          scheduledTime: scheduledAt,
          mediaType: row.media_type ?? undefined,
          coverImageUrl: row.cover_image_url ?? undefined,
          videoDuration: row.video_duration_seconds ?? undefined,
          videoAspectRatio: row.video_aspect_ratio ?? undefined,
          title: row.title ?? undefined,
        },
      });
      if (scheduleError) {
        const message = await ServiceError.extractEdgeFunctionMessage(
          scheduleError,
          scheduleError.message,
        );
        throw new ServiceError('SCHEDULE_FAILED', message);
      }
      // `pending_review` is a successful governed outcome for a team member:
      // the trust gate accepted the request and intentionally held the post
      // for an approver. Treating it as a generic schedule failure hides the
      // actual workflow and invites duplicate retries.
      if (scheduleResult?.status !== 'scheduled' && scheduleResult?.status !== 'pending_review') {
        throw new ServiceError(
          'SCHEDULE_FAILED',
          'The publishing service did not confirm the scheduled state.',
        );
      }

      // Re-read after the server transition so callers observe the target-safe
      // row (for example, stamped captions/readiness), not the preflight copy.
      const { data: refreshed, error: refreshError } = await this.client
        .from('social_posts')
        .select('*')
        .eq('id', id)
        .single();
      if (refreshError || !refreshed) throw refreshError ?? ServiceError.notFound('Post');
      return refreshed;
    }, 'Failed to schedule post');
    if (result.error && pausedState.postId && pausedState.platforms.length > 0) {
      await this.restoreMediaKeys(pausedState.postId, pausedState.platforms);
    }
    return result;
  }

  /**
   * Fetch a post's platform_schedules map plus the fields needed to reason about
   * its enabled/send set. Shared by the per-platform scheduling mutations below.
   */
  private async loadScheduleContext(id: string): Promise<{
    row: PostRow;
    schedules: PlatformSchedulesMap;
    enabled: string[];
    disabled: string[];
  }> {
    const { data, error } = await this.client
      .from('social_posts')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    if (!data) throw ServiceError.notFound('Post');

    const schedules = parsePlatformSchedules(data.platform_schedules);
    const mediaKeys = mediaKeysObject(data.media_keys);
    const disabled = getDisabledPlatforms(mediaKeys);
    const enabled = enabledPlatforms(data.platforms ?? [], mediaKeys);
    return { row: data, schedules, enabled, disabled };
  }

  private async pauseSkippedPlatforms(
    row: PostRow,
    skipped: string[],
  ): Promise<{ updated_at: string }> {
    const current = (mediaKeysObject(row.media_keys) ?? {}) as Record<string, unknown>;
    const next = skipped.reduce<Record<string, unknown>>(
      (keys, platform) => withPlatformEnabled(keys, platform, false),
      current,
    );
    const { data, error } = await this.client
      .from('social_posts')
      .update({ media_keys: next as PostUpdate['media_keys'] })
      .eq('id', row.id)
      .eq('updated_at', row.updated_at)
      .select('id, updated_at')
      .single();
    if (error) {
      if (error.code === 'PGRST116') {
        throw ServiceError.conflict('This post was edited elsewhere. Reopen it and try again.');
      }
      throw error;
    }
    return { updated_at: data?.updated_at ?? row.updated_at };
  }

  private async restoreMediaKeys(postId: string, pausedPlatforms: string[]): Promise<void> {
    const { data: current, error: readError } = await this.client
      .from('social_posts')
      .select('media_keys')
      .eq('id', postId)
      .single();
    if (readError || !current) {
      logger.error('Could not read the post to restore paused platforms after a failed schedule', {
        postId,
        error: readError?.message,
      });
      return;
    }
    const restored = pausedPlatforms.reduce<Record<string, unknown>>(
      (keys, platform) => withPlatformEnabled(keys, platform, true),
      { ...(mediaKeysObject(current.media_keys) ?? {}) } as Record<string, unknown>,
    );
    const { error } = await this.client
      .from('social_posts')
      .update({
        media_keys: (Object.keys(restored).length > 0
          ? restored
          : null) as PostUpdate['media_keys'],
      })
      .eq('id', postId);
    if (error) {
      logger.error('Could not restore paused platforms after a failed schedule', {
        postId,
        error: error.message,
      });
    }
  }

  private partitionScheduleReadiness(
    row: PostRow,
    platforms: string[],
    options: { connected?: Map<string, string | null>; disabled?: string[] } = {},
  ): SchedulePartition {
    return partitionScheduleReadiness(
      platforms.map((platform) => {
        const result = withUnsupportedPublishBlock(
          evaluatePlatformReadiness({
            platform,
            caption: resolvePlatformCaption(row.content, row.platform_captions, platform),
            mediaUrls: row.media_urls ?? [],
            mediaType: row.media_type,
            connected: options.connected ? options.connected.has(platform) : true,
            disabled: options.disabled?.includes(platform) ?? false,
            rowStatus: 'scheduled',
            retryCount: 0,
            videoDurationSeconds: row.video_duration_seconds ?? null,
            perPlatform: perPlatformConfigFor(row.media_keys, platform),
            accountType: options.connected?.get(platform) ?? null,
          }),
        );
        return {
          platform,
          canPublish: result.canPublish,
          blockingMessage: result.blockingIssues[0]?.message ?? null,
        };
      }),
    );
  }

  private assertScheduleReadiness(
    row: PostRow,
    platforms: string[],
    options: { connected?: Map<string, string | null>; disabled?: string[] } = {},
  ): void {
    const failures = platforms
      .map((platform) =>
        evaluatePlatformReadiness({
          platform,
          caption: resolvePlatformCaption(row.content, row.platform_captions, platform),
          mediaUrls: row.media_urls ?? [],
          mediaType: row.media_type,
          connected: options.connected ? options.connected.has(platform) : true,
          disabled: options.disabled?.includes(platform) ?? false,
          rowStatus: 'scheduled',
          retryCount: 0,
          videoDurationSeconds: row.video_duration_seconds ?? null,
          perPlatform: perPlatformConfigFor(row.media_keys, platform),
          // A5: readiness fails CLOSED without the connection's account_type —
          // omitting it rejected Story scheduling even for BUSINESS accounts.
          accountType: options.connected?.get(platform) ?? null,
        }),
      )
      .filter((result) => !result.canPublish);

    if (failures.length === 0) return;

    const first = failures[0];
    throw ServiceError.validationError(
      first.blockingIssues[0]?.message ?? `${first.platform} is not ready to schedule.`,
      { platform: first.platform, issues: first.issues },
    );
  }

  private async loadConnectedPlatforms(
    userId: string,
    platforms: string[],
  ): Promise<Map<string, string | null>> {
    if (platforms.length === 0) return new Map();

    const { data, error } = await this.client
      .from('user_connections')
      .select('platform, account_type')
      .eq('user_id', userId)
      .eq('is_active', true)
      .in('platform', platforms);

    if (error) throw error;

    return new Map(
      (data ?? [])
        .filter(
          (connection): connection is { platform: string; account_type: string | null } =>
            typeof connection.platform === 'string',
        )
        .map((connection) => [connection.platform, connection.account_type ?? null]),
    );
  }

  /**
   * Give one platform its own send time (staggered publishing). Partial write:
   * merges an entry into `platform_schedules[platform]` with the supplied ISO
   * time and status `scheduled`, leaving every other platform (and the row-level
   * `scheduled_at` shared fallback) untouched. Platforms stay attached; the row
   * remains `scheduled`. Absent-entry platforms keep inheriting the row time.
   */
  async setPlatformScheduleTime(
    id: string,
    platform: string,
    scheduledAt: string,
  ): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      const { row, schedules, disabled } = await this.loadScheduleContext(id);
      const connected = await this.loadConnectedPlatforms(row.user_id, [platform]);
      this.assertScheduleReadiness(row, [platform], { connected, disabled });

      const existing = schedules[platform];
      const next: PlatformSchedulesMap = {
        ...schedules,
        [platform]: {
          ...existing,
          scheduled_at: scheduledAt,
          status: existing?.status ?? 'scheduled',
        },
      };

      const updateResponse = await this.updatePost(id, {
        platform_schedules: next as unknown as PostUpdate['platform_schedules'],
      });
      if (updateResponse.error) throw new Error(updateResponse.error.message);
      return updateResponse.data!;
    }, 'Failed to set platform schedule time');
  }

  /**
   * Remove one platform's per-platform time override so it falls back to the
   * row-level shared `scheduled_at`. Prunes the whole column to null once the
   * last override is cleared, restoring the byte-for-byte non-staggered path.
   */
  async clearPlatformScheduleTime(id: string, platform: string): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      const { schedules } = await this.loadScheduleContext(id);

      if (!(platform in schedules)) {
        return await this.getUnwrapped(id);
      }

      const next: PlatformSchedulesMap = { ...schedules };
      delete next[platform];

      const updateResponse = await this.updatePost(id, {
        platform_schedules:
          Object.keys(next).length === 0
            ? null
            : (next as unknown as PostUpdate['platform_schedules']),
      });
      if (updateResponse.error) throw new Error(updateResponse.error.message);
      return updateResponse.data!;
    }, 'Failed to clear platform schedule time');
  }

  /**
   * Drop every per-platform override in one shot — the post goes back to a
   * single shared time (all enabled platforms fire at row `scheduled_at`).
   */
  async clearAllPlatformSchedules(id: string): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      const updateResponse = await this.updatePost(id, {
        platform_schedules: null,
      });
      if (updateResponse.error) throw new Error(updateResponse.error.message);
      return updateResponse.data!;
    }, 'Failed to clear platform schedules');
  }

  /**
   * Whole-post drag: shift the row `scheduled_at` AND every per-platform time by
   * the same delta, preserving the stagger between platforms. A null-schedules
   * post just moves its row time (unchanged path). Only per-platform entries
   * that carry an own `scheduled_at` are shifted; their status/lifecycle fields
   * are left intact.
   */
  async shiftPostSchedule(id: string, deltaMs: number): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      const { row, schedules } = await this.loadScheduleContext(id);

      const shiftIso = (iso: string): string =>
        new Date(new Date(iso).getTime() + deltaMs).toISOString();

      const nextScheduledAt = row.scheduled_at ? shiftIso(row.scheduled_at) : row.scheduled_at;

      const scheduleKeys = Object.keys(schedules);
      const nextSchedules: PlatformSchedulesMap | null =
        scheduleKeys.length === 0
          ? null
          : scheduleKeys.reduce((acc, platform) => {
              const entry = schedules[platform];
              acc[platform] = { ...entry, scheduled_at: shiftIso(entry.scheduled_at) };
              return acc;
            }, {} as PlatformSchedulesMap);

      const updateResponse = await this.updatePost(id, {
        scheduled_at: nextScheduledAt,
        platform_schedules:
          nextSchedules === null
            ? null
            : (nextSchedules as unknown as PostUpdate['platform_schedules']),
      });
      if (updateResponse.error) throw new Error(updateResponse.error.message);
      return updateResponse.data!;
    }, 'Failed to shift post schedule');
  }

  /** Fetch a row and throw on any error, returning the bare row (no envelope). */
  private async getUnwrapped(id: string): Promise<PostRow> {
    const { data, error } = await this.client
      .from('social_posts')
      .select('*')
      .eq('id', id)
      .single();
    if (error) throw error;
    if (!data) throw ServiceError.notFound('Post');
    return data;
  }

  /**
   * Unschedule a post — cancel its pending sends.
   *
   * Common (null-`platform_schedules`) path is unchanged: a scheduled post has
   * NOT been posted to any platform yet, so unscheduling is a pure state flip —
   * status → 'draft', scheduled_at → null. `plan_slot_id` is preserved so the
   * post stays linked to its project task; it just leaves the calendar's
   * scheduled view and reappears in drafts. Inverse of schedulePost.
   *
   * Staggered/half-sent path (per-platform lifecycle present): cancel only the
   * PENDING entries (per-platform status `scheduled`) and leave any already
   * `published`/terminal entries alone — a live platform must never be
   * "unpublished". If nothing has fired yet, the row goes back to `draft` with
   * `platform_schedules` cleared (fully editable again). If at least one entry
   * is already published/terminal, the pending entries are dropped from the map
   * and the row is rolled up (to `partially_published`/`published`/`failed`),
   * keeping the live platforms intact.
   */
  async unschedulePost(id: string): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      if (isPreviewAccountFlagged()) {
        throw new ServiceError('PREVIEW_ACCOUNT', PREVIEW_ACCOUNT_MESSAGE);
      }
      const { row, schedules, enabled } = await this.loadScheduleContext(id);
      if (row.status === 'publishing') {
        throw ServiceError.conflict('This post is already publishing.', {
          postId: id,
          reason: 'already_publishing',
        });
      }

      const hasSchedules = Object.keys(schedules).length > 0;
      const anyLive =
        hasSchedules &&
        enabled.some((p) => {
          const s = effectivePlatformStatus(p, schedules, row.status);
          return s !== 'scheduled';
        });

      if (!hasSchedules || !anyLive) {
        const updateResponse = await this.updatePost(id, {
          status: 'draft',
          scheduled_at: null,
          ...(hasSchedules ? { platform_schedules: null } : {}),
          expectedUpdatedAt: row.updated_at ?? undefined,
        });
        if (updateResponse.error) throw new Error(updateResponse.error.message);
        return updateResponse.data!;
      }

      const remaining: PlatformSchedulesMap = {};
      for (const [platform, entry] of Object.entries(schedules)) {
        if (entry.status !== 'scheduled') remaining[platform] = entry;
      }

      const rolledUp = rollupRowStatus(enabled, remaining, row.status);

      const updateResponse = await this.updatePost(id, {
        status: rolledUp ?? 'partially_published',
        expectedUpdatedAt: row.updated_at ?? undefined,
        platform_schedules:
          Object.keys(remaining).length === 0
            ? null
            : (remaining as unknown as PostUpdate['platform_schedules']),
      });
      if (updateResponse.error) throw new Error(updateResponse.error.message);
      return updateResponse.data!;
    }, 'Failed to unschedule post');
  }

  /**
   * Get post metrics
   */
  async getPostMetrics(postId: string): Promise<ServiceResponse<PostMetrics>> {
    return this.execute(async () => {
      const postResponse = await this.getPost(postId);
      if (!postResponse.data) {
        throw ServiceError.notFound('Post');
      }

      const post = postResponse.data;
      const m = (post.metrics as Record<string, number> | null) || {};
      const totalLikes = m.likes || m.like_count || 0;
      const totalComments = m.comments || m.comment_count || 0;
      const totalShares = m.shares || m.share_count || 0;
      const totalSaves = m.saves || m.save_count || 0;
      const totalViews = m.views || m.video_views || m.impressions || 0;
      const totalEngagement = totalLikes + totalComments + totalShares + totalSaves;

      const metrics: PostMetrics = {
        totalLikes,
        totalComments,
        totalShares,
        totalSaves,
        totalViews,
        engagementRate: totalViews ? (totalEngagement / totalViews) * 100 : 0,
      };

      return metrics;
    });
  }

  /**
   * Real per-post analytics for the inspector — F-134.
   *
   * `social_posts.metrics` (jsonb) is usually empty; the authoritative
   * per-post numbers live in `post_metrics_daily` (FK post_id → social_posts.id),
   * written by the fetch-post-metrics cron as daily snapshots per platform.
   * We take the latest snapshot per platform (rows ordered metric_day desc,
   * platform metrics are cumulative) and sum across platforms. Returns only
   * non-zero fields so the grid never renders a wall of zeros, plus `hasData`
   * so the caller can show an honest "no analytics yet" state vs nothing.
   */
  async getPostDailyMetrics(postId: string): Promise<
    ServiceResponse<{
      metrics: Record<string, number>;
      hasData: boolean;
      byPlatform: Record<string, Record<string, number>>;
      byDestination: Record<string, Record<string, number>>;
      destinations: Array<{ targetId: string; platform: string; label: string }>;
      coverage: { reportingDestinations: number; totalDestinations: number };
    }>
  > {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data: post, error: postErr } = await this.client
        .from('social_posts')
        .select('id')
        .eq('id', postId)
        .maybeSingle();
      if (postErr) throw postErr;
      if (!post) {
        return {
          metrics: {},
          hasData: false,
          byPlatform: {},
          byDestination: {},
          destinations: [],
          coverage: { reportingDestinations: 0, totalDestinations: 0 },
        };
      }

      const { data, error } = await this.client
        .from('post_metrics_daily_unified')
        .select(
          'target_id, connection_id, platform, metric_day, likes, comments, shares, reach, views, saves, clicks',
        )
        .eq('post_id', postId)
        .order('metric_day', { ascending: false });
      if (error) throw error;

      const rows = data ?? [];
      const { data: destinations, error: destinationsError } = await this.client
        .from('social_post_targets')
        .select('id, connection_id, platform')
        .eq('post_id', postId)
        .in('delivery_status', ['published', 'published_with_warnings']);
      if (destinationsError) throw destinationsError;

      // A target is the immutable delivery unit. Resolve a human-readable
      // connection name solely for display; aggregation keeps its identity at
      // target grain so renamed/reconnected accounts cannot merge analytics.
      const connectionIds = [...new Set((destinations ?? []).map((item) => item.connection_id))];
      const { data: connections, error: connectionsError } = connectionIds.length
        ? await this.client
            .from('user_connections')
            .select('id, display_name, platform_username, platform_user_id')
            .in('id', connectionIds)
        : { data: [], error: null };
      if (connectionsError) throw connectionsError;
      const connectionLabelById = new Map(
        (connections ?? []).map((connection) => [
          connection.id,
          connection.display_name ?? connection.platform_username ?? connection.platform_user_id,
        ]),
      );

      const legacyRows: LegacyPostMetricRow[] = [];
      const targetRows: TargetPostMetricRow[] = [];
      for (const row of rows) {
        if (!row.platform || !row.metric_day) continue;
        const base = {
          platform: row.platform,
          metric_day: row.metric_day,
          likes: row.likes,
          comments: row.comments,
          shares: row.shares,
          reach: row.reach,
          views: row.views,
          saves: row.saves,
          clicks: row.clicks,
        };
        if (row.target_id && row.connection_id) {
          targetRows.push({ ...base, target_id: row.target_id, connection_id: row.connection_id });
        } else {
          legacyRows.push(base);
        }
      }

      const aggregate = aggregateTargetAwareMetrics({
        legacyRows,
        targetRows,
        destinations: (destinations ?? [])
          .filter(
            (destination) =>
              !!destination.id && !!destination.connection_id && !!destination.platform,
          )
          .map((destination) => ({
            target_id: destination.id,
            connection_id: destination.connection_id,
            platform: destination.platform,
          })),
      });

      const onlyReported = (source: Record<string, number>) =>
        Object.fromEntries(Object.entries(source).filter(([, value]) => value > 0));
      const byPlatform = Object.fromEntries(
        Object.entries(aggregate.byPlatform)
          .map(([platform, metric]) => [platform, onlyReported(metric)])
          .filter(([, metric]) => Object.keys(metric).length > 0),
      );
      const byDestination = Object.fromEntries(
        Object.entries(aggregate.byDestination)
          .map(([targetId, metric]) => [targetId, onlyReported(metric)])
          .filter(([, metric]) => Object.keys(metric).length > 0),
      );
      const destinationDetails = (destinations ?? [])
        .filter((destination) => !!destination.id && !!destination.platform)
        .map((destination) => ({
          targetId: destination.id,
          platform: destination.platform,
          label: connectionLabelById.get(destination.connection_id) ?? 'Connected account',
        }));

      return {
        metrics: onlyReported(aggregate.metrics),
        hasData: rows.length > 0,
        byPlatform,
        byDestination,
        destinations: destinationDetails,
        coverage: aggregate.coverage,
      };
    }, 'Failed to load post metrics');
  }

  /**
   * Daily engagement time-series for one post (likes + comments + shares per
   * `metric_day`), oldest → newest. Platform-aware callers get the selected
   * delivery family only; omitting platform preserves the legacy cross-platform
   * rollup. Powers the per-post "performance over time" sparkline. Needs ≥2
   * days to chart, so callers hide the spark when sparse.
   */
  async getPostMetricsSeries(
    postId: string,
    platform?: string | null,
  ): Promise<ServiceResponse<{ day: string; value: number }[]>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data: post } = await this.client
        .from('social_posts')
        .select('id')
        .eq('id', postId)
        .maybeSingle();
      if (!post) return [];

      let query = this.client
        .from('post_metrics_daily_rollup')
        .select('metric_day, likes, comments, shares')
        .eq('post_id', postId)
        .order('metric_day', { ascending: true });
      const selectedPlatform = platform?.trim().toLowerCase();
      if (selectedPlatform) query = query.eq('platform', selectedPlatform);
      const { data, error } = await query;
      if (error) throw error;

      // The selected-platform path is the post anatomy authority. The
      // no-platform branch remains as a compatibility rollup for older callers.
      const byDay = new Map<string, number>();
      for (const r of data ?? []) {
        const eng = (r.likes ?? 0) + (r.comments ?? 0) + (r.shares ?? 0);
        byDay.set(r.metric_day, (byDay.get(r.metric_day) ?? 0) + eng);
      }
      return [...byDay.entries()].map(([day, value]) => ({ day, value }));
    }, 'Failed to load post metrics series');
  }

  /**
   * Bulk update post status
   */
  async bulkUpdateStatus(postIds: string[], status: string): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      if (isPreviewAccountFlagged()) {
        throw new ServiceError('PREVIEW_ACCOUNT', PREVIEW_ACCOUNT_MESSAGE);
      }
      const userId = await this.ensureAuthenticated();

      const { error } = await this.client.from('social_posts').update({ status }).in('id', postIds);

      if (error) throw error;

      // Clear cache
      this.clearPostCaches(userId);
    }, 'Failed to bulk update status');
  }

  /**
   * Clear all post-related caches for a user. Public so chat tool views
   * (e.g. schedule-content-view) can flush the in-memory cache when the
   * agent mutates posts server-side and the client needs the calendar /
   * scheduled list to reflect the change immediately rather than after
   * the 2-minute TTL.
   */
  public clearPostCaches(userId: string): void {
    this.invalidateCacheByPattern(`posts-${userId}-`);
    this.invalidateCacheByPattern(`filtered-posts-${userId}-`);
    this.invalidateCacheByPattern(`posts-paginated-${userId}-`);
  }

  /**
   * Get posts by product ID
   */
  async getPostsByProduct(
    productId: string,
    options?: QueryOptions,
  ): Promise<ServiceResponse<PostRow[]>> {
    return this.getFilteredPosts({ productId }, options);
  }

  /**
   * Get posts by project ID
   */
  async getPostsByProject(
    projectId: string,
    options?: QueryOptions,
  ): Promise<ServiceResponse<PostRow[]>> {
    return this.getFilteredPosts({ projectId }, options);
  }

  /**
   * Update post metrics (likes, comments, etc)
   */
  async updatePostMetrics(
    id: string,
    metrics: Partial<{
      likes: number;
      comments: number;
      shares: number;
      saves: number;
      video_views: number;
    }>,
  ): Promise<ServiceResponse<PostRow>> {
    return this.updatePost(id, metrics);
  }

  /**
   * Create a plain draft post. Callers (BriefingCard approve, calendar
   * Duplicate on grid + Posts table) referenced postService.createDraft, a
   * method that did not exist — a compile-time break + a dead Duplicate action
   * (beta scorecard P0 #1). Accepts a single `platform` or a `platforms[]`
   * (duplicate carries the source post's full set); resolves the shared-account
   * owner and sanitizes content exactly like createDraftWithGeneratedImages.
   */
  async createDraft(input: {
    content: string;
    platform?: string;
    platforms?: string[];
    mediaUrls?: string[];
    mediaType?: PostInsert['media_type'];
    projectId?: string;
  }): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const ownerId = await this.resolveOwnerId();
      const activeKitId = await this.resolveActiveBrandKitId();

      const content = (input.content ?? '')
        .replace(/<script[^>]*>.*?<\/script>/gi, '')
        .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
        .trim();

      const platforms =
        input.platforms && input.platforms.length > 0
          ? input.platforms
          : [input.platform ?? 'instagram'];
      const mediaUrls = input.mediaUrls ?? [];

      const postData: PostInsert = {
        user_id: ownerId,
        authored_by: userId,
        content,
        platforms,
        status: 'draft',
        media_urls: mediaUrls,
        // Derive the CHECK-constrained media_type from the url list (never a
        // raw MIME); rule (a): >1 media of any mix is a carousel.
        media_type: input.mediaType ?? (mediaUrls.length > 0 ? inferMediaType(mediaUrls) : null),
        // Stamp the active workspace at creation, same pattern as
        // CampaignService.createCampaign.
        brand_kit_id: activeKitId,
        ...(input.projectId ? { campaign_id: input.projectId } : {}),
      };

      const { data, error } = await this.client
        .from('social_posts')
        .insert(postData)
        .select()
        .single();

      if (error) throw error;
      this.clearPostCaches(userId);
      return data;
    }, 'Failed to create draft');
  }

  /**
   * Create a draft with generated images
   */
  async createDraftWithGeneratedImages(input: CreateDraftInput): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const ownerId = await this.resolveOwnerId();
      const activeKitId = await this.resolveActiveBrandKitId();

      // If generated images are provided, transfer them to Supabase Storage
      let finalMediaUrls = input.mediaUrls || [];

      if (input.generatedImages && input.generatedImages.length > 0) {
        const transferResponse = await mediaService.saveGeneratedImagesForDraft(
          input.generatedImages,
        );

        if (!transferResponse.data || transferResponse.data.length === 0) {
          throw ServiceError.operationError('Failed to transfer generated images');
        }

        // Combine existing media URLs with new generated image URLs
        finalMediaUrls = [...finalMediaUrls, ...transferResponse.data];
      }

      // Validate and sanitize content
      const sanitizeContent = (content: string): string => {
        if (!content || !content.trim()) {
          throw ServiceError.validationError('Post content is required');
        }
        // Remove any script tags and dangerous HTML
        return content
          .replace(/<script[^>]*>.*?<\/script>/gi, '')
          .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
          .trim();
      };

      const postData: PostInsert = {
        user_id: ownerId,
        authored_by: userId,
        content: sanitizeContent(input.content),
        platforms: [input.platform || 'instagram'],
        status: 'draft',
        media_urls: finalMediaUrls,
        shopify_product_id: input.productId,
        // Stamp the active workspace at creation, same pattern as
        // CampaignService.createCampaign.
        brand_kit_id: activeKitId,
        ...(effectiveCampaignId(input) ? { campaign_id: effectiveCampaignId(input) } : {}),
        collection_id: input.collectionId,
      };

      const { data, error } = await this.client
        .from('social_posts')
        .insert(postData)
        .select()
        .single();

      if (error) throw error;

      // Clear cache
      this.clearPostCaches(userId);

      return data;
    }, 'Failed to create draft with generated images');
  }

  /**
   * Create a draft post that genuinely originates from a Concept, and stamp
   * its organic lineage — the write half of the growth-intelligence spine
   * (build spec §2, ORGANIC). Modeled directly on
   * `createDraftWithGeneratedImages` above (same image-transfer step and
   * content sanitizer). Unlike a freeform draft, workspace, approved current
   * copy, and destinations are inherited from the Concept's parent Brief and
   * declared organic intents. Mutable active-workspace UI state cannot
   * relabel a lineage-bearing post.
   *
   * `campaign_id` is deliberately NEVER set here — see `CreatePostFromConceptInput`.
   */
  async createPostFromConcept(
    input: CreatePostFromConceptInput,
  ): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const ownerId = await this.resolveOwnerId();

      if (!input.conceptRenditionId) {
        throw ServiceError.validationError('conceptRenditionId is required');
      }

      // Resolve the Concept (`project_tasks` row) behind this rendition and
      // its currently-accepted ad unit. RLS on `concept_renditions`/
      // `project_tasks` scopes these reads to rows the caller can already
      // see — this is a lineage lookup, not a trust boundary. If either
      // fails to resolve, this is not a "post from a Concept" at all, so we
      // fail loudly rather than silently falling back to a freeform post.
      const { data: rendition, error: renditionError } = await this.client
        .from('concept_renditions')
        .select('id, project_task_id')
        .eq('id', input.conceptRenditionId)
        .maybeSingle();

      if (renditionError) throw renditionError;
      if (!rendition) {
        throw ServiceError.notFound('Concept rendition');
      }

      const { data: conceptTask, error: conceptError } = await this.client
        .from('project_tasks')
        .select('id, project_id, current_ad_unit_id')
        .eq('id', rendition.project_task_id)
        .maybeSingle();

      if (conceptError) throw conceptError;
      if (!conceptTask) {
        throw ServiceError.notFound('Concept');
      }

      if (!conceptTask.current_ad_unit_id) {
        throw ServiceError.validationError(
          'Choose and approve the current copy before publishing this Concept.',
        );
      }

      const { data: currentAdUnit, error: currentAdUnitError } = await this.client
        .from('ad_units')
        .select('id, project_task_id, status')
        .eq('id', conceptTask.current_ad_unit_id)
        .maybeSingle();
      if (currentAdUnitError) throw currentAdUnitError;
      if (
        !currentAdUnit ||
        currentAdUnit.project_task_id !== conceptTask.id ||
        currentAdUnit.status !== 'approved'
      ) {
        throw ServiceError.validationError(
          'The current copy must be approved and belong to this Concept before publishing.',
        );
      }

      const { data: parentBrief, error: parentBriefError } = await this.client
        .from('projects')
        .select('id, kind, user_id, brand_kit_id')
        .eq('id', conceptTask.project_id)
        .maybeSingle();
      if (parentBriefError) throw parentBriefError;
      if (
        !parentBrief ||
        parentBrief.kind !== 'brief' ||
        parentBrief.user_id !== ownerId ||
        !parentBrief.brand_kit_id
      ) {
        throw ServiceError.validationError(
          'This Concept is not scoped to a publishable Brief workspace.',
        );
      }

      const { data: intentRows, error: intentsError } = await this.client
        .from('project_task_delivery_intents')
        .select('intent, platform')
        .eq('project_task_id', conceptTask.id);
      if (intentsError) throw intentsError;
      const declaredOrganicPlatforms = new Set(
        (intentRows ?? [])
          .filter((row) => row.intent === 'organic' && Boolean(row.platform?.trim()))
          .map((row) => row.platform!.trim().toLowerCase()),
      );
      const platforms = (input.platforms ?? []).map((platform) => platform.trim().toLowerCase());
      if (platforms.length === 0 || declaredOrganicPlatforms.size === 0) {
        throw ServiceError.validationError(
          'This Concept needs an explicit organic delivery platform before publishing.',
        );
      }
      if (platforms.some((platform) => !declaredOrganicPlatforms.has(platform))) {
        throw ServiceError.validationError(
          'A requested platform is not declared as an organic destination for this Concept.',
        );
      }

      // Transfer generated images to Storage first — identical to
      // createDraftWithGeneratedImages above.
      let finalMediaUrls = input.mediaUrls || [];
      if (input.generatedImages && input.generatedImages.length > 0) {
        const transferResponse = await mediaService.saveGeneratedImagesForDraft(
          input.generatedImages,
        );

        if (!transferResponse.data || transferResponse.data.length === 0) {
          throw ServiceError.operationError('Failed to transfer generated images');
        }

        finalMediaUrls = [...finalMediaUrls, ...transferResponse.data];
      }

      const content = (input.content ?? '')
        .replace(/<script[^>]*>.*?<\/script>/gi, '')
        .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
        .trim();

      if (!content) {
        throw ServiceError.validationError('Post content is required');
      }

      const postData: PostInsert = {
        user_id: ownerId,
        authored_by: userId,
        content,
        platforms,
        status: 'draft',
        media_urls: finalMediaUrls,
        media_type: finalMediaUrls.length > 0 ? inferMediaType(finalMediaUrls) : null,
        shopify_product_id: input.productId,
        collection_id: input.collectionId,
        // A Concept-origin post inherits its Brief workspace.
        brand_kit_id: parentBrief.brand_kit_id,
        // Both references are validated above; neither degrades to a guess.
        concept_rendition_id: rendition.id,
        ad_unit_id: currentAdUnit.id,
      };

      const { data, error } = await this.client
        .from('social_posts')
        .insert(postData)
        .select()
        .single();

      if (error) throw error;
      this.clearPostCaches(userId);
      return data;
    }, 'Failed to create post from concept');
  }

  /**
   * Update a draft with new generated images
   */
  async updateDraftWithGeneratedImages(
    draftId: string,
    generatedImages: SecureImageData[],
  ): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      const _userId = await this.ensureAuthenticated();

      // Get existing draft
      const existingDraft = await this.getPost(draftId);
      if (!existingDraft.data) {
        throw ServiceError.notFound('Draft');
      }

      // Transfer generated images to Supabase Storage
      const transferResponse = await mediaService.saveGeneratedImagesForDraft(generatedImages);

      if (!transferResponse.data || transferResponse.data.length === 0) {
        throw ServiceError.operationError('Failed to transfer generated images');
      }

      // Combine existing media URLs with new generated image URLs
      const existingMediaUrls = existingDraft.data.media_urls || [];
      const updatedMediaUrls = [...existingMediaUrls, ...transferResponse.data];

      // Update the draft with new media URLs
      return this.updatePost(draftId, {
        media_urls: updatedMediaUrls,
      });
    }, 'Failed to update draft with generated images');
  }

  /**
   * Search posts by content
   */
  async searchPosts(
    searchTerm: string,
    options?: QueryOptions,
  ): Promise<ServiceResponse<PostRow[]>> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      return { error: ServiceError.unauthorized(), data: null };
    }

    return this.execute(async () => {
      let query = this.client.from('social_posts').select('*').ilike('content', `%${searchTerm}%`);

      // Scope to the active workspace, keeping unattributed (legacy,
      // pre-workspace-stamping) rows visible — see resolveActiveBrandKitId.
      const activeKitId = await this.resolveActiveBrandKitId();
      if (activeKitId) {
        query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
      }

      // Apply query options
      query = this.buildQuery(query, {
        orderBy: 'created_at',
        orderDirection: 'desc',
        ...options,
      });

      const { data, error } = await query;
      if (error) throw error;

      return data || [];
    }, 'Failed to search posts');
  }

  /**
   * Get posts for specific social account
   */
  async getPostsByAccount(
    accountId: string,
    options?: QueryOptions,
  ): Promise<ServiceResponse<PostRow[]>> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      return { error: ServiceError.unauthorized(), data: null };
    }

    return this.execute(async () => {
      let query = this.client.from('social_posts').select('*').eq('social_account_id', accountId);

      // Scope to the active workspace, keeping unattributed (legacy,
      // pre-workspace-stamping) rows visible — see resolveActiveBrandKitId.
      const activeKitId = await this.resolveActiveBrandKitId();
      if (activeKitId) {
        query = query.or(`brand_kit_id.eq.${activeKitId},brand_kit_id.is.null`);
      }

      query = this.buildQuery(query, {
        orderBy: 'created_at',
        orderDirection: 'desc',
        ...options,
      });

      const { data, error } = await query;
      if (error) throw error;

      return data || [];
    }, 'Failed to get posts by account');
  }

  async getCompetitorPosts(
    competitorIds: string[],
    options: {
      pageParam?: number;
      pageSize?: number;
      searchQuery?: string;
      dateRange?: { from?: Date; to?: Date };
      sortBy?: string;
    },
  ): Promise<ServiceResponse<{ posts: PostRow[]; nextPage: number | null }>> {
    const { pageParam = 0, pageSize = 20, searchQuery, dateRange, sortBy = 'date' } = options;

    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const cacheKey = `competitor-posts-${userId}-${competitorIds.join(
        ',',
      )}-${JSON.stringify(options)}`;

      return this.executeWithCache(
        cacheKey,
        async () => {
          const from = pageParam * pageSize;
          const to = from + pageSize - 1;

          let query = this.client
            .from('social_posts')
            .select('*', { count: 'exact' })
            .in('competitor_id', competitorIds);

          // Apply search query
          if (searchQuery) {
            query = query.ilike('content', `%${searchQuery}%`);
          }

          // Apply date range
          if (dateRange?.from) {
            query = query.gte('created_at', dateRange.from.toISOString());
          }
          if (dateRange?.to) {
            query = query.lte('created_at', dateRange.to.toISOString());
          }

          // Apply sorting
          switch (sortBy) {
            case 'engagement':
              query = query.order('metrics->>totalLikes', { ascending: false, nullsFirst: false });
              break;
            case 'relevance':
              if (searchQuery) {
                query = query.order('content', { ascending: true });
              } else {
                query = query.order('created_at', { ascending: false });
              }
              break;
            case 'date':
            default:
              query = query.order('created_at', { ascending: false });
              break;
          }

          // Apply pagination last
          query = query.range(from, to);

          const { data, error, count } = await query;

          if (error) {
            throw error;
          }

          const nextPage = to < (count ?? 0) - 1 ? pageParam + 1 : null;
          return { posts: data || [], nextPage };
        },
        { ttl: 5 * 60 * 1000 },
      );
    }, 'Failed to get competitor posts');
  }

  /**
   * Create multiple drafts from PostData array - OPTIMIZED VERSION
   */
  async bulkCreateDrafts(
    posts: Array<{
      content: string;
      platform: string;
      accountId?: string;
      mediaUrls?: string[];
      generatedImages?: SecureImageData[];
      productId?: string;
      projectId?: string;
      collectionId?: string;
      scheduledAt?: string;
    }>,
  ): Promise<ServiceResponse<PostRow[]>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();
      const ownerId = await this.resolveOwnerId();
      const activeKitId = await this.resolveActiveBrandKitId();

      // Validate and sanitize content
      const sanitizeContent = (content: string): string => {
        if (!content || !content.trim()) {
          throw ServiceError.validationError('Post content is required');
        }
        return content
          .replace(/<script[^>]*>.*?<\/script>/gi, '')
          .replace(/<iframe[^>]*>.*?<\/iframe>/gi, '')
          .trim();
      };

      // Process all images in parallel first
      const imageProcessingPromises = posts.map(async (post) => {
        if (post.generatedImages && post.generatedImages.length > 0) {
          const transferResponse = await mediaService.saveGeneratedImagesForDraft(
            post.generatedImages,
          );
          return transferResponse.data || [];
        }
        return [];
      });

      const processedImageUrls = await Promise.all(imageProcessingPromises);

      // Prepare all posts for batch insert
      const postsToInsert: PostInsert[] = posts.map((post, index) => {
        const finalMediaUrls = [...(post.mediaUrls || []), ...processedImageUrls[index]];

        return {
          user_id: ownerId,
          authored_by: userId, // the authenticated actor (member or owner)
          content: sanitizeContent(post.content),
          platforms: [post.platform || 'instagram'],
          // Scheduled rows become due only after exact targets are materialized
          // below. Inserting them as scheduled first would reopen the legacy
          // parent-dispatch race this cutover is designed to close.
          status: 'draft',
          scheduled_at: null,
          media_urls: finalMediaUrls,
          shopify_product_id: post.productId,
          // Stamp the active workspace at creation, same pattern as
          // CampaignService.createCampaign.
          brand_kit_id: activeKitId,
          ...(post.projectId ? { campaign_id: post.projectId } : {}),
          collection_id: post.collectionId,
        };
      });

      const { data, error } = await this.client.from('social_posts').insert(postsToInsert).select();

      if (error) throw error;

      const created = data ?? [];
      const scheduledResults = await Promise.all(
        created.map(async (row, index) => {
          const scheduledAt = posts[index]?.scheduledAt;
          if (!scheduledAt) return row;
          const scheduled = await this.schedulePost(row.id, scheduledAt);
          if (scheduled.error || !scheduled.data) {
            throw new Error(scheduled.error?.message ?? 'Failed to materialize scheduled targets');
          }
          return scheduled.data;
        }),
      );

      // Clear cache
      this.clearPostCaches(userId);

      return scheduledResults;
    }, 'Failed to create bulk drafts');
  }

  /**
   * Schedule multiple posts with specific dates and times - OPTIMIZED VERSION
   */
  async bulkSchedulePosts(
    posts: Array<{
      id?: string; // For existing posts
      content?: string; // For new posts
      platform: string;
      accountId: string;
      scheduledAt: string;
      mediaUrls?: string[];
      generatedImages?: SecureImageData[];
      productId?: string;
      projectId?: string;
    }>,
  ): Promise<ServiceResponse<PostRow[]>> {
    return this.execute(async () => {
      if (isPreviewAccountFlagged()) {
        throw new ServiceError('PREVIEW_ACCOUNT', PREVIEW_ACCOUNT_MESSAGE);
      }
      const userId = await this.ensureAuthenticated();

      // Separate existing posts to update vs new posts to create
      const existingPosts = posts.filter((p) => p.id);
      const newPosts = posts.filter((p) => !p.id);

      const results: PostRow[] = [];

      // Existing rows use the same target-first scheduling command as the
      // Inspector; no bulk path may write status='scheduled' directly.
      if (existingPosts.length > 0) {
        const updateResults = await Promise.all(
          existingPosts.map((post) => this.schedulePost(post.id!, post.scheduledAt)),
        );
        updateResults.forEach((result) => {
          if (result.error) throw new Error(result.error.message);
          if (result.data) results.push(result.data);
        });
      }

      // Batch create new scheduled posts
      if (newPosts.length > 0) {
        const createResult = await this.bulkCreateDrafts(
          newPosts.map((post) => ({
            content: post.content!,
            platform: post.platform,
            accountId: post.accountId,
            mediaUrls: post.mediaUrls,
            generatedImages: post.generatedImages,
            productId: post.productId,
            projectId: post.projectId,
            scheduledAt: post.scheduledAt,
          })),
        );

        if (createResult.data) {
          results.push(...createResult.data);
        }
      }

      // Clear cache
      this.clearPostCaches(userId);

      return results;
    }, 'Failed to bulk schedule posts');
  }

  /**
   * Update engagement metrics for multiple posts - OPTIMIZED VERSION
   */
  async bulkUpdateEngagement(
    updates: Array<{
      id: string;
      likes?: number;
      comments?: number;
      shares?: number;
      saves?: number;
      video_views?: number;
    }>,
  ): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      const userId = await this.ensureAuthenticated();

      // Batch update all posts at once using a transaction-like approach
      const updatePromises = updates.map((update) => {
        const { id, ...metrics } = update;
        return this.client.from('social_posts').update(metrics).eq('id', id);
      });

      const results = await Promise.all(updatePromises);

      // Check if any updates failed
      const failedUpdates = results.filter((result) => result.error);
      if (failedUpdates.length > 0) {
        throw ServiceError.operationError(`Failed to update ${failedUpdates.length} posts`);
      }

      // Clear cache
      this.clearPostCaches(userId);
    }, 'Failed to bulk update engagement');
  }

  /**
   * Clear all cached data (useful after creating/updating posts)
   */
  clearCache(): void {
    // Since we're using executeWithCache from BaseService,
    // we need to clear at the instance level
    // For now, we'll rely on cache invalidation at the React Query level
    if (import.meta.env.DEV) logger.debug('🧹 PostService cache cleared');
  }

  /**
   * Atomically increment regenerate_count for a saved post. Returns the new
   * count or null if the post was not found (e.g. still a workbench temp id).
   */
  async incrementRegenerateCount(postId: string): Promise<number | null> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data: current, error: readErr } = await this.client
        .from('social_posts')
        .select('regenerate_count')
        .eq('id', postId)
        .maybeSingle();
      if (readErr) throw readErr;
      if (!current) return null;
      const next = (current.regenerate_count ?? 0) + 1;
      const { data, error } = await this.client
        .from('social_posts')
        .update({ regenerate_count: next })
        .eq('id', postId)
        .select('regenerate_count')
        .single();
      if (error) throw error;
      return (data?.regenerate_count as number | undefined) ?? next;
    }, 'Failed to increment regenerate count');
  }

  /**
   * Read persistent regenerate_count for a saved post — null if not found.
   * Used by the workbench to enforce the 2-attempt budget across reloads.
   */
  async getRegenerateCount(postId: string): Promise<number | null> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data, error } = await this.client
        .from('social_posts')
        .select('regenerate_count')
        .eq('id', postId)
        .maybeSingle();
      if (error) throw error;
      return (data?.regenerate_count as number | undefined) ?? null;
    }, 'Failed to read regenerate count');
  }

  // ── CE-P3b: Org trust review queue ─────────────────────────────────────────
  //
  // getPendingReviewPosts(orgId) lived here and queried `org_members` directly.
  // REMOVED 2026-08-23 with the franchise org layer. It was already unreachable
  // -- its only caller was in useOrganization.ts, deleted in the same pass --
  // and it would have hard-errored the moment `org_members` was dropped.
  //
  // Do not confuse it with AccountMembersService.getPendingReviewPosts(), which
  // takes no arguments, resolves the account owner itself, and IS live (see
  // useAccountApproval.ts). That one is the review queue.

  /**
   * Approve a pending-review post: set status → 'scheduled'.
   * If scheduled_at is in the past (the post was held in review),
   * push it forward by 5 minutes so the next cron cycle picks it up.
   */
  async approvePendingPost(postId: string): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      const { data: current, error: readErr } = await this.client
        .from('social_posts')
        .select('scheduled_at, status')
        .eq('id', postId)
        .single();

      if (readErr) throw new Error(readErr.message);
      if (current.status !== 'pending_review') {
        throw new Error('Post is not in pending_review state');
      }

      const now = Date.now();
      const originalAt = current.scheduled_at ? new Date(current.scheduled_at).getTime() : 0;
      const scheduledAt =
        originalAt > now ? current.scheduled_at! : new Date(now + 5 * 60 * 1000).toISOString();

      const targets = await targetCoverService.materializePostTargets(postId);
      if (targets.error) throw new Error(targets.error.message);
      if (targets.data?.ready !== true) {
        throw new Error(
          targets.data?.readinessIssue ??
            'Review the publishing options for every destination before approving.',
        );
      }

      const { data, error } = await this.client
        .from('social_posts')
        .update({ status: 'scheduled', scheduled_at: scheduledAt })
        .eq('id', postId)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data as PostRow;
    }, 'Failed to approve post');
  }

  /**
   * Reject a pending-review post: send it back to 'draft' so the
   * member can revise and re-submit (or discard it).
   */
  async rejectPendingPost(postId: string): Promise<ServiceResponse<PostRow>> {
    return this.execute(async () => {
      await this.ensureAuthenticated();
      // F-137: clear scheduled_at + approved_at when bouncing back to draft
      // (mirrors unschedulePost). Otherwise the rejected draft keeps its old —
      // often past — scheduled timestamp, which shows a stale time in the
      // calendar/drafts UI and, on re-submit, trips approvePendingPost's
      // "past → push 5 min" branch instead of a clean reschedule. plan_slot_id
      // is intentionally preserved — reject is a soft bounce-back, the project
      // link should survive like an unschedule.
      const { data, error } = await this.client
        .from('social_posts')
        .update({ status: 'draft', scheduled_at: null, approved_at: null })
        .eq('id', postId)
        .select()
        .single();

      if (error) throw new Error(error.message);
      return data as PostRow;
    }, 'Failed to reject post');
  }
}

// Export singleton instance
export const postService = PostService.getInstance();
